import { Component, ViewChild, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MatDialog } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PaymentsService } from '../../services/payments.service';
import { VendorService } from '../../services/vendor.service';
import { PurchasingService } from '../../services/purchasing.service';
import { resolveDefaultBankAccountCode } from '../../utils/bank-account-default';
import {
	DocumentIntakeService,
	ExtractedMemo,
	GroupMode,
	InvoiceRow,
	NaPaymentDefaults,
	VendorInvoiceFields,
	VendorInvoiceCandidate,
	VendorMatch,
} from '../../services/document-intake.service';
import {
	MultipagePromptComponent,
	MultipagePromptResult,
} from '../../shared/multipage-prompt/multipage-prompt.component';
import { buildDashboardThemeVars } from '../../shared/dashboard-widget-theme';
import { NoPoItemsModalComponent } from '../../vendors/vendor-invoice/no-po-items-modal/no-po-items-modal.component';

type ItemStatus = 'pending' | 'posting' | 'posted' | 'failed' | 'skipped';

interface QueueItem {
	id: number;
	fileName: string;
	page?: number;               // source PDF page (per-page streaming) — drives the source viewer
	pages?: number[];            // all source pages when a memo spans more than one page
	memo: ExtractedMemo;
	status: ItemStatus;
	message: string;
	selectedIds: string[];       // open-invoice ids the credit is applied to (editable)
	allocOverrides?: { [invId: string]: number };  // operator-set partial allocation per invoice ($ applied)
	expanded?: boolean;          // body open: undefined = auto (open when exception), true/false = user's choice
	custSearch: string;          // manual customer lookup text (when unmatched)
	custResults: any[];
	custChange?: boolean;        // change-customer search open on an already-matched row
	resolving: boolean;          // loading the picked customer's open invoices
	duplicate?: boolean;         // (vendor, credit memo #) already posted as a type-21 supptrans
	venSearch?: string;          // manual vendor lookup text (correct/override the matched vendor)
	venResults?: any[];
	venOpen?: boolean;           // vendor change box open
	showRaw?: boolean;           // raw AI extraction panel open
	dry?: 'pass' | 'fail';       // last dry-run verdict (does not change status)
	dryMessage?: string;
}

/** Per-page read state — persisted so a mid-batch AI failure (e.g. credits run out) is recoverable. */
type VinvPageState = 'queued' | 'reading' | 'done' | 'failed';

/** Loading stepper for a multi-page PDF — one step per page, persisted with a Resend for failed pages. */
interface VinvLoadJob {
	id: number;
	fileName: string;
	total: number;
	current: number;   // page being read right now (1-based; 0 = idle)
	done: number;
	failed: number;
	lastError?: string;
	steps: VinvPageState[];   // per-page state, index 0 = page 1
	running?: boolean;        // a drain is in flight for this job
}

/** One vendor purchase invoice read from a batch PDF, staged for review/dry-run/post. */
interface VendorInvoiceItem {
	id: number;
	fileName: string;
	page?: number;
	pages?: number[];                   // all source pages when one invoice spans more than one page
	fields: VendorInvoiceFields;
	vendor_match: VendorMatch | null;
	vendor: any | null;                 // full getVendor object once loaded (taxes/exrate/currcode)
	grns: any[];                        // suggested/selected GRN lines (toinvoice/unitprice editable)
	matchedCodes: string[];
	unmatched: string[];                // extracted item codes with no open GRN
	loadingVendor: boolean;
	status: ItemStatus;
	message: string;
	dry?: 'pass' | 'fail';
	dryMessage?: string;
	glPreview?: any[];
	expanded?: boolean;   // body open: undefined = auto (open when exception), true/false = user's choice
	showRaw?: boolean;
	showSource?: boolean;  // source-PDF page panel open (session only — bytes aren't persisted)
	venOpen?: boolean;     // vendor change box open
	venSearch?: string;    // vendor change lookup text
	duplicate?: boolean;   // (supplier, invoice #) already exists in supptrans — already entered
	dupPosted?: number;    // total that was actually posted for the already-entered invoice
	dupTransno?: string;   // transno of the already-entered invoice
	forcePost?: boolean;   // operator chose to post despite a totals mismatch (reviewed the delta)
}

/**
 * Vendor Intake: batch document intake for both vendor CREDITS (credit memos → vendor-backed
 * National Account payments) and vendor INVOICES (purchase invoices → GRN-matched AP entry).
 * Drop one or many PDFs; AI reads each into a review queue. Credits with a clean match can be
 * bulk-posted; invoices pre-fill with suggested GRNs and a per-row dry run before posting.
 */
@Component({
	selector: 'app-credit-memo-intake',
	templateUrl: './credit-memo-intake.component.html',
	styleUrls: ['./credit-memo-intake.component.scss'],
})
export class CreditMemoIntakeComponent {
	/** Which intake the operator is working: vendor credits (existing) or vendor invoices (new). */
	section: 'credit' | 'invoice' = 'invoice';
	private static readonly SECTION_STORE_KEY = 'vendorintake.section.v1';
	color: any;
	queue: QueueItem[] = [];
	processing = 0;
	private seq = 0;

	/** Open AR invoices per customer (the select/unselect pool), keyed by debtorno. */
	openInvoicesByCustomer: { [debtorno: string]: InvoiceRow[] } = {};

	// Run state (shared by dry-run and live post).
	posting = false;
	runMode: 'dry' | 'live' | null = null;
	postedCount = 0;
	failedCount = 0;
	postTotal = 0;

	// File-level backing vendor (operator's pick for the whole spreadsheet).
	fileVendor: { supplierid: string; suppname: string } | null = null;
	fileVendorSearch = '';
	fileVendorResults: any[] = [];
	private vendorSub: any;

	// File-level customer (brand-portal exports are downloaded per-customer, e.g. all-Vans).
	fileCustomer: { debtorno: string; branchcode: string; name: string } | null = null;
	fileCustomerSearch = '';
	fileCustomerResults: any[] = [];

	// Resolved payment defaults for unattended posting.
	private bankaccounts: any[] = [];
	private company: any = null;
	private paymentMethods: any[] = [];
	private config: any = null;
	private defaults: NaPaymentDefaults | null = null;

	constructor(
		private documentIntakeService: DocumentIntakeService,
		private paymentsService: PaymentsService,
		private vendorService: VendorService,
		private purchasingService: PurchasingService,
		private dialog: MatDialog,
		private router: Router,
		private modalService: NgbModal,
		private globalSearchService: GlobalSearchService,
	) {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.bankaccounts.subscribe((a: any) => { this.bankaccounts = a || []; });
		this.globalSearchService.company.subscribe((c: any) => { this.company = c; });
		this.globalSearchService.payment_methods.subscribe((m: any) => { this.paymentMethods = m || []; });
		this.globalSearchService.configsubscription.subscribe((s: any) => { this.config = s; });
		this.globalSearchService.user.subscribe((u: any) => { this.currentUser = u; });
		this.restoreVinvQueue();
		this.restoreVinvJobs();
		this.restoreSection();
	}

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('cmi', this.color || 'blue');
	}

	setSection(next: 'credit' | 'invoice'): void {
		this.section = next;
		try {
			localStorage.setItem(CreditMemoIntakeComponent.SECTION_STORE_KEY, next);
		} catch { /* non-fatal */ }
	}

	private restoreSection(): void {
		try {
			const saved = localStorage.getItem(CreditMemoIntakeComponent.SECTION_STORE_KEY);
			if (saved === 'credit' || saved === 'invoice') {
				this.section = saved;
			} else if (this.vinvQueue.length) {
				this.section = 'invoice';
			}
		} catch { /* non-fatal */ }
	}

	/** Logged-in user payload passed to vendor/invoice create + glpreview (same as the entry screen). */
	private currentUser: any = null;

	// Import mode: PDF (AI extraction) or CSV (mapped, no AI).
	importMode: 'pdf' | 'csv' = 'pdf';

	/** Destination fields the CSV is mapped onto (app-excel-mapper uploadColumns). */
	memoMapping = [
		{ column: 'memo_no', label: 'Memo No (groups lines)', required: true },
		{ column: 'memo_date', label: 'Memo Date', required: false },
		{ column: 'customer_account', label: 'Customer Account No', required: true },
		{ column: 'customer_name', label: 'Customer Name', required: false },
		{ column: 'invoice_ref', label: 'Invoice / Delivery Ref', required: false },
		{ column: 'vendor_name', label: 'Vendor / Supplier Name', required: false },
		{ column: 'description', label: 'Line Description', required: false },
		{ column: 'line_extended', label: 'Line Extended Amount', required: false },
		{ column: 'commission_amount', label: 'Commission (if its own column)', required: false },
		{ column: 'total_amount', label: 'Credit Total / Amount', required: true },
	];

	/** Merge a response's open-invoice pool and queue its memos with their auto-selection. */
	private enqueueMemos(res: any, fileName: string): void {
		if (res?.open_invoices_by_customer) {
			this.openInvoicesByCustomer = { ...this.openInvoicesByCustomer, ...res.open_invoices_by_customer };
		}
		(res.memos as ExtractedMemo[]).forEach(memo => this.enqueueOneMemo(memo, fileName));
	}

	/** Push one matched memo onto the queue (per-page streaming + bulk both funnel here).
	 *  Multi-page single memo: if a page with the SAME memo # from the SAME file is already queued,
	 *  it's a continuation — merge this page's lines into it rather than creating a duplicate card. */
	private enqueueOneMemo(memo: ExtractedMemo, fileName: string, page?: number): void {
		const ref = (memo?.fields?.credit_memo_number || '').trim().toUpperCase();
		if (ref && page != null) {
			const existing = this.queue.find(q => q.fileName === fileName
				&& (q.memo.fields.credit_memo_number || '').trim().toUpperCase() === ref);
			if (existing) {
				this.mergeMemoPage(existing, memo, page);
				return;
			}
		}
		this.queue.push({
			id: ++this.seq,
			fileName,
			page: page != null ? page : (memo as any)?.page,
			memo,
			status: 'pending',
			message: '',
			selectedIds: [...(memo.invoice_selection?.selected_ids || [])],
			custSearch: '',
			custResults: [],
			resolving: false,
		});
		this.cmCheckDuplicate(this.queue[this.queue.length - 1]);
	}

	/** Has this (vendor, credit memo #) already been posted as a National Account credit
	 *  (type-21 supptrans)? Same guard the manual credit screen uses — flags "Already entered". */
	private cmCheckDuplicate(item: QueueItem): void {
		if (item.status === 'posted') { item.duplicate = false; return; }
		const sid = item.memo.vendor_match?.supplierid;
		const ref = (item.memo.fields.credit_memo_number || '').trim();
		if (!sid || !ref) { item.duplicate = false; return; }
		this.vendorService.checkCreditReference(sid, ref).subscribe((r: any) => {
			if (item.status === 'posted') { item.duplicate = false; return; }
			item.duplicate = !!(r && r.used);
		});
	}

	/** Fold a continuation page into an existing memo: append unique line items, and adopt the
	 *  header totals from whichever page carries the larger document total (the grand-total page) so
	 *  a memo split across pages isn't double-counted or under-stated. */
	private mergeMemoPage(item: QueueItem, incoming: ExtractedMemo, page: number): void {
		const f = item.memo.fields;
		const inc = incoming.fields || ({} as any);
		// Append continuation product lines (commission lines are already stripped server-side).
		const lineKey = (l: any) => `${(l.itemcode || '').trim()}|${(l.description || '').trim()}|${l.extended}`;
		const seen = new Set((f.lines || []).map(lineKey));
		(inc.lines || []).forEach((l: any) => { if (!seen.has(lineKey(l))) { (f.lines = f.lines || []).push(l); seen.add(lineKey(l)); } });
		// The grand document total prints once (on the page with the "Total" line) → take the largest.
		// Commission lines are listed PER PAGE → they're additive across pages, so SUM them.
		// AR relief = grand total − total commission (recomputed so a split memo isn't mis-stated).
		const total = Math.max(Math.abs(Number(f.total_amount) || 0), Math.abs(Number(inc.total_amount) || 0));
		const commission = (Number(f.commission_amount) || 0) + (Number(inc.commission_amount) || 0);
		f.total_amount = total;
		f.commission_amount = commission;
		f.relief_amount = Math.round((total - commission) * 100) / 100;
		item.memo.ar_receipt = f.relief_amount;
		// A confident match on either page wins (vendor/customer can print on just one page).
		if (incoming.vendor_match?.auto && !item.memo.vendor_match?.auto) { item.memo.vendor_match = incoming.vendor_match; }
		if (incoming.customer_match?.auto && !item.memo.customer_match?.auto) {
			item.memo.customer_match = incoming.customer_match;
			item.selectedIds = [...(incoming.invoice_selection?.selected_ids || item.selectedIds)];
		}
		item.pages = [...(item.pages || (item.page != null ? [item.page] : [])), page].sort((a, b) => a - b);
		this.cmCheckDuplicate(item);
	}

	// ── Manual vendor override (per-memo) — correct a mis-matched/ambiguous vendor ──
	cmSearchVendor(item: QueueItem, term: string): void {
		const q = (term || '').trim();
		if (this.vendorSub) { this.vendorSub.unsubscribe?.(); }
		if (q.length < 2) { item.venResults = []; return; }
		this.vendorSub = this.vendorService.getVendorSearch(q).subscribe((r: any) => {
			let rows: any[] = [];
			if (Array.isArray(r)) { rows = r; }
			else if (Array.isArray(r?.rows)) { rows = r.rows; }
			else if (Array.isArray(r?.vendors)) { rows = r.vendors; }
			else if (Array.isArray(r?.suppliers)) { rows = r.suppliers; }
			item.venResults = rows.slice(0, 25);
		});
	}
	cmPickVendor(item: QueueItem, v: any): void {
		const supplierid = v?.supplierid || v?.supplier_id || v?.id || '';
		const suppname = v?.suppname || v?.name || String(supplierid);
		item.memo.vendor_match = { supplierid, suppname, currcode: v?.currcode || '', score: 1, auto: true };
		item.memo.confidence = { ...item.memo.confidence, vendor: 1 };
		item.venResults = [];
		item.venOpen = false;
		item.venSearch = '';
		this.cmCheckDuplicate(item);
	}

	// ── Manual customer resolve (per-memo name pick) ────────────────────────
	searchRowCustomer(item: QueueItem, term: string): void {
		const q = (term || '').trim();
		if (q.length < 2) { item.custResults = []; return; }
		this.documentIntakeService.searchCustomers(q).subscribe((r: any) => {
			item.custResults = (r?.rows || []).slice(0, 25);
		});
	}

	/** Operator picks the customer for this memo → set it, load that customer's open invoices,
	 *  auto-select. No mapping is saved; the pick applies only to this row. */
	pickRowCustomer(item: QueueItem, cust: any): void {
		if (!cust?.debtorno || item.resolving) { return; }
		item.resolving = true;
		item.memo.customer_match = {
			debtorno: cust.debtorno,
			branchcode: cust.branchcode || '',
			name: cust.name || '',
			score: 1,
			auto: true,
		};
		item.memo.confidence = { ...item.memo.confidence, customer: 1 };
		this.documentIntakeService.customerInvoices(cust.debtorno).subscribe(
			(res: any) => {
				item.resolving = false;
				if (!res?.success) {
					this.globalSearchService.showNotification(res?.message || 'Could not load invoices', 'danger', 'bottom', 'left');
					return;
				}
				this.openInvoicesByCustomer[cust.debtorno] = res.open_invoices || [];
				item.selectedIds = this.autoSelectIds(item);
				item.custResults = [];
				this.globalSearchService.showNotification(
					`Matched to ${cust.name} (${cust.debtorno})`, 'success', 'bottom', 'left'
				);
			},
			() => {
				item.resolving = false;
				this.globalSearchService.showNotification('Could not load invoices — server error', 'danger', 'bottom', 'left');
			}
		);
	}

	/** Client-side auto-select: reference match, else a single exact-balance invoice. */
	private autoSelectIds(item: QueueItem): string[] {
		const pool = this.customerInvoices(item);
		if (!pool.length) { return []; }
		const norm = (s: any) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
		const toks = [item.memo.fields.source_invoice_ref, item.memo.fields.po_number]
			.map(norm).filter(t => t.length >= 4);
		const ar = Number(item.memo.ar_receipt) || 0;
		const refHits = pool.filter(inv => {
			const hay = norm(inv.transno + inv.orderno + inv.reference);
			return toks.some(t => hay.indexOf(t) >= 0);
		});
		if (refHits.length) { return refHits.map(i => i.id); }
		const exact = pool.filter(inv => Math.abs(inv.leftto - ar) <= 0.01);
		return exact.length === 1 ? [exact[0].id] : [];
	}

	// ── Custom spreadsheet mapping (replaces the generic mapper for this screen) ──
	csvFileName = '';
	csvHeaders: string[] = [];
	csvRows: any[] = [];
	columnMap: { [target: string]: string } = {};   // target field -> source header
	mappingReady = false;
	parsing = false;          // reading/parsing the spreadsheet
	matching = false;         // matching memos to customers/invoices (batched)
	matchedCount = 0;
	matchTotal = 0;
	private static readonly MATCH_CHUNK = 150;   // memos per match request

	/** Header-name synonyms per target field (ordered by preference) for auto-detect. */
	private synonyms: { [k: string]: string[] } = {
		memo_no: ['memono', 'creditno', 'creditnumber', 'memonumber', 'documentno', 'docno'],
		memo_date: ['memodate', 'creditdate', 'invoicedate', 'date'],
		customer_account: ['accountno', 'shiptoid', 'shipto', 'customerid', 'custno', 'custid', 'debtorno'],
		customer_name: ['accountname', 'customername', 'customerlocation', 'shiptoname', 'billtoname', 'billto'],
		// NOTE: no bare 'supplier' — "Supplier No" in tire memos is a PRODUCT code, not the vendor.
		vendor_name: ['manufacturer', 'vendorname', 'suppliername', 'brand'],
		invoice_ref: ['ponumber', 'pono', 'ordernumber', 'orderno', 'deliveryreceiptno', 'deliveryreceipt', 'invoiceno', 'reference', 'pickticketnumber'],
		total_amount: ['credittotal', 'creditamount', 'amount', 'total', 'balance'],
		commission_amount: ['commissionamount', 'commission'],
		description: ['description', 'itemdescription', 'productdescription', 'desc'],
		line_extended: ['extended', 'extendedprice', 'linetotal', 'lineamount', 'extendedamount'],
	};

	async onSpreadsheetSelected(files: FileList | File[] | null): Promise<void> {
		const file = files && (files as any)[0] ? (files as any)[0] as File : null;
		if (!file) { return; }
		this.parsing = true;
		try {
			const buf = await file.arrayBuffer();
			// Yield a frame so the loader paints before the (sync) parse of a large sheet.
			await new Promise(r => setTimeout(r, 0));
			const wb = XLSX.read(buf, { type: 'array' });
			const sheet = wb.Sheets[wb.SheetNames[0]];
			const aoa: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
			// Build rows keyed by TRIMMED headers — CSVs often have "a, b, c" (spaces after
			// commas), which would otherwise key cells by " b" and break every lookup.
			const rawHeaders: string[] = (aoa[0] || []).map((h: any) => String(h).trim());
			this.csvHeaders = rawHeaders.filter((h: string) => h !== '');
			this.csvRows = aoa.slice(1)
				.filter((arr: any[]) => Array.isArray(arr) && arr.some(c => String(c).trim() !== ''))
				.map((arr: any[]) => {
					const o: any = {};
					rawHeaders.forEach((h, i) => { if (h !== '') { o[h] = arr[i]; } });
					return o;
				});
			this.csvFileName = file.name;
			this.autoDetectColumns();
			this.mappingReady = true;
		} catch {
			this.globalSearchService.showNotification('Could not read that spreadsheet', 'danger', 'bottom', 'left');
		} finally {
			this.parsing = false;
		}
	}

	private norm(s: any): string { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

	/** Auto-map each target field to the best-matching source header (synonyms, no reuse). */
	autoDetectColumns(): void {
		const used = new Set<string>();
		this.columnMap = {};
		this.memoMapping.forEach(c => this.columnMap[c.column] = '');   // default every field to none
		for (const col of this.memoMapping) {
			const syns = this.synonyms[col.column] || [this.norm(col.column)];
			let best: string | null = null;
			let bestRank = 999;
			for (const h of this.csvHeaders) {
				if (used.has(h)) { continue; }
				const nh = this.norm(h);
				syns.forEach((syn, rank) => {
					if (rank < bestRank && (nh === syn || nh.indexOf(syn) >= 0 || syn.indexOf(nh) >= 0)) {
						bestRank = rank; best = h;
					}
				});
			}
			if (best) { this.columnMap[col.column] = best; used.add(best); }
		}
	}

	mappedCount(): number {
		return Object.values(this.columnMap).filter(v => !!v).length;
	}
	mappingValid(): boolean {
		return this.memoMapping.filter(c => c.required).every(c => !!this.columnMap[c.column]);
	}
	resetMapping(): void {
		this.mappingReady = false;
		this.csvHeaders = [];
		this.csvRows = [];
		this.columnMap = {};
		this.csvFileName = '';
	}

	/** Build target-keyed rows from the mapping and feed the existing aggregator. */
	importMapped(): void {
		if (!this.mappingValid()) {
			this.globalSearchService.showNotification('Map Memo No, Customer Account and Credit Total first', 'warning', 'bottom', 'left');
			return;
		}
		const rows = this.csvRows.map(r => {
			const o: any = {};
			for (const target of Object.keys(this.columnMap)) {
				const src = this.columnMap[target];
				if (src) { o[target] = r[src]; }
			}
			return o;
		});
		this.onRowsMapped(rows);
		this.resetMapping();
	}

	get readyCount(): number {
		return this.queue.filter(q => q.status === 'pending' && this.isReady(q)).length;
	}

	// ── Per-memo invoice selection (the batch "select invoices" step) ───────
	customerInvoices(item: QueueItem): InvoiceRow[] {
		const d = item.memo.customer_match?.debtorno;
		return d ? (this.openInvoicesByCustomer[d] || []) : [];
	}
	isSelected(item: QueueItem, inv: InvoiceRow): boolean {
		return item.selectedIds.indexOf(inv.id) >= 0;
	}
	toggleInvoice(item: QueueItem, inv: InvoiceRow): void {
		const i = item.selectedIds.indexOf(inv.id);
		if (i >= 0) {
			item.selectedIds.splice(i, 1);
			if (item.allocOverrides) { delete item.allocOverrides[inv.id]; }   // drop any partial set for it
		} else {
			item.selectedIds.push(inv.id);
		}
	}
	/**
	 * Allocate the credit across the selected invoices. Each invoice takes its operator-set partial
	 * amount when one exists (clamped to its balance), otherwise auto FIFO fills from what's left of
	 * the credit (oldest first, capped at balance). Partial allocations are honoured — an invoice can
	 * stay open when the credit only covers part of it (the rest comes from another vendor).
	 */
	computeToRows(item: QueueItem): Array<{ id: string; transno: string; leftto: number; allocation: number }> {
		const pool = this.customerInvoices(item);
		const chosen = pool.filter(inv => item.selectedIds.indexOf(inv.id) >= 0)
			.sort((a, b) => String(a.trandate).localeCompare(String(b.trandate)));
		const overrides = item.allocOverrides || {};
		let remaining = Math.round((Number(item.memo.ar_receipt) || 0) * 100) / 100;
		const rows: Array<{ id: string; transno: string; leftto: number; allocation: number }> = [];
		for (const inv of chosen) {
			const leftto = Math.round((Number(inv.leftto) || 0) * 100) / 100;
			let take: number;
			if (overrides[inv.id] != null) {
				// Operator-set partial: clamp to the invoice balance (never settles more than is open).
				take = Math.max(0, Math.min(leftto, Math.round((Number(overrides[inv.id]) || 0) * 100) / 100));
			} else {
				take = Math.round(Math.min(leftto, Math.max(remaining, 0)) * 100) / 100;
			}
			remaining = Math.round((remaining - take) * 100) / 100;
			if (take <= 0) { continue; }
			rows.push({ id: inv.id, transno: inv.transno, leftto: inv.leftto, allocation: take });
		}
		return rows;
	}
	allocatedTotal(item: QueueItem): number {
		return this.computeToRows(item).reduce((s, r) => s + r.allocation, 0);
	}
	/** Operator sets how much of the credit to apply to one invoice (partial). Clamped to its balance. */
	cmSetAllocation(item: QueueItem, inv: InvoiceRow, value: any): void {
		const v = parseFloat(value);
		const leftto = Math.round((Number(inv.leftto) || 0) * 100) / 100;
		const amt = isNaN(v) ? 0 : Math.max(0, Math.min(leftto, Math.round(v * 100) / 100));
		item.allocOverrides = { ...(item.allocOverrides || {}), [inv.id]: amt };
		if (amt > 0 && item.selectedIds.indexOf(inv.id) < 0) { item.selectedIds.push(inv.id); }
	}
	/** The amount we'll apply to a given invoice right now (for the row input + line styling). */
	cmAllocationFor(item: QueueItem, inv: InvoiceRow): number {
		const r = this.computeToRows(item).find(x => x.id === inv.id);
		return r ? r.allocation : 0;
	}
	/** Credit fully consumed by invoices (whole receipt lands). Now a NOTE, not a posting gate. */
	fullyApplied(item: QueueItem): boolean {
		return Math.abs(this.allocatedTotal(item) - (Number(item.memo.ar_receipt) || 0)) <= 0.01
			&& (Number(item.memo.ar_receipt) || 0) > 0;
	}
	/** Something is applied to at least one invoice — the receipt posts for that amount (partial OK). */
	cmApplied(item: QueueItem): boolean { return this.allocatedTotal(item) > 0.001; }
	/** Ready to post = vendor + customer confident, an amount applied, and NOT already entered.
	 *  Partial application is allowed — the receipt is sized to what's applied; the invoice stays open. */
	isReady(item: QueueItem): boolean {
		return !!item.memo.vendor_match?.auto && !!item.memo.customer_match?.auto
			&& !item.duplicate && !this.isDebitMemo(item) && this.cmApplied(item);
	}

	/** A DEBIT memo is the vendor billing money BACK, so it settles against a customer
	 *  CREDIT, not an open invoice — and this batch takes amounts as absolute values, which
	 *  would post it backwards. Keep it out of the batch and send it to payment entry. */
	isDebitMemo(item: QueueItem): boolean {
		return item?.memo?.fields?.document_type === 'debit_memo';
	}

	// ══════════════ Credit workbench: triage + stats + journal (mirrors the vinv* invoice tab) ══════════════

	/** OK to post: an amount is applied to at least one invoice (partial is fine — invoice stays open). */
	cmReconciles(item: QueueItem): boolean { return this.cmApplied(item); }

	/** Triage: 'posted' (fades) · 'exception' (needs a human) · 'ready' (clean, postable). */
	cmState(item: QueueItem): 'posted' | 'exception' | 'ready' {
		if (item.status === 'posted') { return 'posted'; }
		if (item.status === 'failed' || item.status === 'skipped' || item.dry === 'fail' || item.duplicate) { return 'exception'; }
		return this.isReady(item) ? 'ready' : 'exception';
	}
	cmShowBody(item: QueueItem): boolean {
		// Tri-state: once the operator clicks, their choice wins; until then exceptions auto-open.
		if (item.expanded != null) { return item.expanded; }
		return this.cmState(item) === 'exception';
	}

	get cmExceptionRows(): QueueItem[] { return this.queue.filter(q => this.cmState(q) === 'exception'); }
	get cmReadyRows(): QueueItem[] { return this.queue.filter(q => this.cmState(q) === 'ready'); }
	get cmPostedRows(): QueueItem[] { return this.queue.filter(q => this.cmState(q) === 'posted'); }
	get cmSortedRows(): QueueItem[] { return [...this.cmExceptionRows, ...this.cmReadyRows, ...this.cmPostedRows]; }
	get cmPostableRows(): QueueItem[] {
		return this.queue.filter(q => this.isReady(q) && q.status !== 'posted' && q.status !== 'posting');
	}

	cmReviewReason(item: QueueItem): string {
		if (item.status === 'posted') { return 'Posted'; }
		if (item.status === 'failed') { return 'Failed'; }
		if (item.status === 'skipped') { return 'Skipped'; }
		if (item.duplicate) { return 'Already entered'; }
		if (this.isDebitMemo(item)) { return 'Debit memo'; }
		if (!item.memo.vendor_match?.auto) { return 'Match vendor'; }
		if (!item.memo.customer_match?.auto) { return 'Match customer'; }
		if (!this.cmApplied(item)) { return 'Apply an amount'; }
		if (item.dry === 'pass') { return 'Preview OK'; }
		if (!this.fullyApplied(item)) { return 'Partial apply'; }
		return 'Ready';
	}

	cmIssues(item: QueueItem): string[] {
		const out: string[] = [];
		if (item.resolving) { out.push('Loading…'); return out; }
		if (item.duplicate) { out.push('Already entered'); }
		if (this.isDebitMemo(item)) { out.push('Debit memo — key this in payment entry against the customer credit'); }
		if (!item.memo.vendor_match?.auto) { out.push('Match the vendor'); }
		if (!item.memo.customer_match?.auto) { out.push('Match the customer'); }
		if (item.memo.customer_match?.auto && !item.selectedIds.length) { out.push('Select invoices'); }
		if (item.memo.customer_match?.auto && item.selectedIds.length && !this.cmApplied(item)) { out.push('Nothing applied'); }
		if (this.cmInBatchDup(item)) { out.push('Double scan'); }
		if (item.status === 'failed') { out.push('Post failed'); }
		if (item.status === 'skipped') { out.push('Skipped'); }
		if (item.dry === 'fail') { out.push('Dry run would fail'); }
		return out;
	}

	/** Another row in THIS batch with the same vendor + credit-memo number (a double scan). */
	cmInBatchDup(item: QueueItem): boolean {
		const ref = (item.memo.fields.credit_memo_number || '').trim().toUpperCase();
		const ven = (item.memo.vendor_match?.suppname || item.memo.fields.vendor_name || '').trim().toUpperCase();
		if (!ref) { return false; }
		return this.queue.some(q => q !== item
			&& (q.memo.fields.credit_memo_number || '').trim().toUpperCase() === ref
			&& (q.memo.vendor_match?.suppname || q.memo.fields.vendor_name || '').trim().toUpperCase() === ven);
	}

	// Batch stats / journal — "Credit $" = AR-relief that posts; "Applied $" = allocated to invoices.
	get cmInvoiceTotal(): number { return this.queue.reduce((s, q) => s + (Number(q.memo.ar_receipt) || 0), 0); }
	get cmEnteredTotal(): number { return this.queue.reduce((s, q) => s + this.allocatedTotal(q), 0); }
	get cmPostedCount(): number { return this.queue.filter(q => q.status === 'posted').length; }
	get cmBatchDupCount(): number { return this.queue.filter(q => this.cmInBatchDup(q)).length; }
	get cmDupCount(): number { return this.queue.filter(q => q.status !== 'posted' && q.duplicate).length; }
	get cmMismatchCount(): number { return this.queue.filter(q => q.memo.customer_match?.auto && !this.fullyApplied(q)).length; }
	cmReconcileBatch(): boolean { return Math.abs(this.cmInvoiceTotal - this.cmEnteredTotal) <= 0.01; }

	cmDetail(item: QueueItem): string {
		if (item.status === 'failed' || item.status === 'skipped') { return item.message || 'Post failed'; }
		if (item.status === 'posted') { return item.message ? ('ref ' + item.message) : ''; }
		if (item.dry === 'fail') { return item.dryMessage || 'Dry run would fail'; }
		return '';
	}

	/** Raw extraction + match diagnostics for the card's "Show raw AI" panel. */
	cmRaw(item: QueueItem): string {
		try {
			return JSON.stringify({
				fields: item.memo.fields,
				vendor_match: item.memo.vendor_match,
				customer_match: item.memo.customer_match,
				ar_receipt: item.memo.ar_receipt,
				match_debug: (item.memo as any).match_debug,
			}, null, 2);
		} catch { return ''; }
	}

	/** Page label — a range ("p8–9") when a memo spanned multiple pages, else "p8". */
	cmPageLabel(item: QueueItem): string {
		if (item.pages && item.pages.length > 1) { return 'p' + item.pages[0] + '–' + item.pages[item.pages.length - 1]; }
		return item.page ? 'p' + item.page : '';
	}

	/** Open the dropped credit-memo PDF at this row's page in the shared source-viewer modal. */
	cmHasSource(item: QueueItem): boolean { return !!(item.fileName && this.vinvPdfSrc[item.fileName]); }
	cmViewSource(item: QueueItem): void {
		const src = item.fileName ? this.vinvPdfSrc[item.fileName] : '';
		if (!src || !this.vinvSourceModalTpl) { return; }
		this.vinvSource = { src, title: item.fileName + (item.page ? ' · page ' + item.page : ''), page: item.page || 1 };
		this.modalService.open(this.vinvSourceModalTpl, { size: 'xl', ariaLabelledBy: 'vinv-source-title', windowClass: 'vinv-source-modal' });
	}

	private cmJournalRows(): Array<{ idx: number; memo: string; vendor: string; customer: string; source: string; creditAmt: number; appliedAmt: number; status: string; detail: string }> {
		return this.queue.map((q, i) => ({
			idx: i + 1,
			memo: q.memo.fields.credit_memo_number || '',
			vendor: q.memo.vendor_match?.suppname || q.memo.fields.vendor_name || '',
			customer: q.memo.customer_match?.name || q.memo.fields.customer_name || '',
			source: (q.fileName || '') + (q.page ? ' p' + q.page : ''),
			creditAmt: Number(q.memo.ar_receipt) || 0,
			appliedAmt: this.allocatedTotal(q),
			status: this.cmReviewReason(q),
			detail: this.cmDetail(q),
		}));
	}

	exportCmJournalXlsx(): void {
		const rows = this.cmJournalRows();
		if (!rows.length) { this.globalSearchService.showNotification('No memos in the batch to export', 'info', 'bottom', 'left'); return; }
		const aoa: any[][] = [['#', 'Memo', 'Vendor', 'Customer', 'Source', 'Credit $', 'Applied $', 'Status', 'Detail']];
		rows.forEach(r => aoa.push([r.idx, r.memo, r.vendor, r.customer, r.source, r.creditAmt, r.appliedAmt, r.status, r.detail]));
		aoa.push([]);
		aoa.push(['', '', '', '', 'Grand total', this.cmInvoiceTotal, this.cmEnteredTotal, '', '']);
		const ws = XLSX.utils.aoa_to_sheet(aoa);
		ws['!cols'] = [{ wch: 4 }, { wch: 18 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 40 }];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Credit batch journal');
		XLSX.writeFile(wb, 'vendor-credit-batch-journal-' + this.vinvExportStamp() + '.xlsx');
	}

	exportCmJournalPdf(): void {
		const rows = this.cmJournalRows();
		if (!rows.length) { this.globalSearchService.showNotification('No memos in the batch to export', 'info', 'bottom', 'left'); return; }
		const doc = new jsPDF({ orientation: 'landscape' });
		doc.setFontSize(14);
		doc.text('Vendor Credit Batch Journal', 14, 16);
		doc.setFontSize(9);
		doc.text(new Date().toLocaleString(), 14, 22);
		autoTable(doc, {
			startY: 26,
			head: [['#', 'Memo', 'Vendor', 'Customer', 'Source', 'Credit $', 'Applied $', 'Status', 'Detail']],
			body: rows.map(r => [r.idx, r.memo, r.vendor, r.customer, r.source, this.vinvMoney2(r.creditAmt), this.vinvMoney2(r.appliedAmt), r.status, r.detail]),
			foot: [['', '', '', '', 'Grand total', this.vinvMoney2(this.cmInvoiceTotal), this.vinvMoney2(this.cmEnteredTotal), '', '']],
			styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
			headStyles: { fillColor: [33, 33, 33] },
			footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
			columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' } },
		});
		doc.save('vendor-credit-batch-journal-' + this.vinvExportStamp() + '.pdf');
	}
	private vinvMoney2(n: number): string { return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' }); }

	// Per-row single-send — posts the National Account payment in place (mirrors vinvDryRun/vinvPost).
	cmDryRun(item: QueueItem): Promise<void> { return this.cmRunOne(item, true); }
	cmPost(item: QueueItem): Promise<void> { return this.cmRunOne(item, false); }

	private async cmRunOne(item: QueueItem, dryRun: boolean): Promise<void> {
		if (this.posting || item.status === 'posting') { return; }
		const defaults = this.resolveDefaults();
		if (!defaults) {
			this.globalSearchService.showNotification('Missing payment defaults (bank/currency/method) or National Account GL config — cannot run', 'danger', 'bottom', 'left');
			return;
		}
		try {
			const methodName = this.paymentMethods[0]?.paymentname || '';
			const journals: any = await this.paymentsService.getJournals({ type: methodName }).toPromise();
			if (Array.isArray(journals) && journals.length) { defaults.journalno = journals[0]; }
		} catch { /* server surfaces a department/journal error if needed */ }

		const payload = this.documentIntakeService.buildNaPayment(item.memo, defaults, this.computeToRows(item), dryRun);
		if (!payload) {
			if (dryRun) { item.dry = 'fail'; item.dryMessage = 'Could not build payment'; }
			else { item.status = 'skipped'; item.message = 'Could not build payment'; }
			return;
		}
		if (!dryRun) { item.status = 'posting'; item.message = ''; }
		try {
			const res: any = await this.paymentsService.nationalAccountPayment(payload).toPromise();
			if (res?.success) {
				if (dryRun) { item.dry = 'pass'; item.dryMessage = 'Preview passed'; }
				else { item.status = 'posted'; item.message = res.reference_id || ''; item.dry = undefined; item.duplicate = false; }
			} else {
				if (dryRun) { item.dry = 'fail'; item.dryMessage = res?.message || 'Preview failed'; }
				else { item.status = 'failed'; item.message = res?.message || 'Post failed'; }
			}
		} catch {
			if (dryRun) { item.dry = 'fail'; item.dryMessage = 'Server error'; }
			else { item.status = 'failed'; item.message = 'Server error'; }
		}
	}

	/** Loading-stepper step states for the credit tab (mirrors vinvStepState/vinvStepArray). */
	cmStepState(job: VinvLoadJob, pageNum: number): 'done' | 'active' | 'pending' {
		if (pageNum < job.current) { return 'done'; }
		if (pageNum === job.current) { return 'active'; }
		return 'pending';
	}
	cmStepArray(job: VinvLoadJob): number[] { return Array.from({ length: job.total }, (_, i) => i + 1); }

	// ── File-level backing vendor ───────────────────────────────────────────
	searchFileVendor(term: string): void {
		const q = (term || '').trim();
		if (this.vendorSub) { this.vendorSub.unsubscribe?.(); }
		if (q.length < 2) { this.fileVendorResults = []; return; }
		this.vendorSub = this.vendorService.getVendorSearch(q).subscribe((r: any) => {
			let rows: any[] = [];
			if (Array.isArray(r)) { rows = r; }
			else if (Array.isArray(r?.rows)) { rows = r.rows; }
			else if (Array.isArray(r?.vendors)) { rows = r.vendors; }
			else if (Array.isArray(r?.suppliers)) { rows = r.suppliers; }
			this.fileVendorResults = rows.slice(0, 50);
		});
	}
	onFileVendorPicked(v: any): void {
		const supplierid = v?.supplierid || v?.supplier_id || v?.id || '';
		const suppname = v?.suppname || v?.name || String(supplierid);
		this.fileVendor = { supplierid, suppname };
		this.fileVendorSearch = suppname;
	}
	clearFileVendor(): void {
		this.fileVendor = null;
		this.fileVendorSearch = '';
		this.fileVendorResults = [];
	}

	// ── File-level customer (whole-file backing customer) ───────────────────
	searchFileCustomer(term: string): void {
		const q = (term || '').trim();
		if (q.length < 2) { this.fileCustomerResults = []; return; }
		this.documentIntakeService.searchCustomers(q).subscribe((r: any) => {
			this.fileCustomerResults = (r?.rows || []).slice(0, 25);
		});
	}
	onFileCustomerPicked(c: any): void {
		this.fileCustomer = { debtorno: c?.debtorno || '', branchcode: c?.branchcode || '', name: c?.name || '' };
		this.fileCustomerSearch = this.fileCustomer.name;
	}
	clearFileCustomer(): void {
		this.fileCustomer = null;
		this.fileCustomerSearch = '';
		this.fileCustomerResults = [];
	}

	get rememberedMode(): GroupMode | null {
		return this.documentIntakeService.getSplitPref();
	}
	clearRemembered(): void {
		this.documentIntakeService.clearSplitPref();
		this.globalSearchService.showNotification('Split/single will be asked again per file', 'info', 'bottom', 'left');
	}

	onFilesDropped(files: FileList | File[] | null): void {
		const list = files ? Array.from(files as any) as File[] : [];
		const pdfs = list.filter(f => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
		if (!pdfs.length) {
			this.globalSearchService.showNotification('Drop one or more PDF credit memos', 'warning', 'bottom', 'left');
			return;
		}
		pdfs.forEach(f => this.processFile(f));
	}

	/** Credit-side loading steppers — one per in-flight file, mirrors the invoice tab's vinvJobs. */
	cmJobs: VinvLoadJob[] = [];
	private cmJobSeq = 0;
	dismissCmJob(job: VinvLoadJob): void { this.cmJobs = this.cmJobs.filter(j => j !== job); }

	private async processFile(file: File): Promise<void> {
		this.processing++;
		let job: VinvLoadJob | null = null;
		try {
			const mode = await this.resolveGroupMode(file);
			if (!mode) { this.processing--; return; }
			const b64 = await this.documentIntakeService.fileToBase64(file);
			this.vinvPdfSrc[file.name] = b64;  // retain for the shared source-page viewer

			// 'single' → whole PDF is one memo (one call). 'split' → stream a page at a time so
			// memos appear as each call returns and the stepper tracks progress, like invoices.
			if (mode === 'single') {
				job = { id: ++this.cmJobSeq, fileName: file.name, total: 1, current: 1, done: 0, failed: 0, steps: ['reading'] };
				this.cmJobs.push(job);
				try {
					const res: any = await this.documentIntakeService.extractCreditMemo(b64, 'single').toPromise();
					if (res?.success && res.memos?.length) {
						this.enqueueMemos(res, file.name);
						job.done = 1;
					} else {
						job.failed = 1; job.lastError = res?.message || 'could not read';
					}
				} catch (e: any) {
					job.failed = 1; job.lastError = e?.error?.message || e?.message || 'extraction failed';
				}
			} else {
				const pages = await this.documentIntakeService.getPdfPageCount(file);
				const total = Math.max(1, pages || 1);
				job = {
					id: ++this.cmJobSeq, fileName: file.name, total, current: 0, done: 0, failed: 0,
					steps: Array.from({ length: total }, () => 'queued' as VinvPageState),
				};
				this.cmJobs.push(job);
				for (let p = 1; p <= total; p++) {
					job.current = p;
					try {
						const res: any = await this.documentIntakeService.extractCreditMemoPage(b64, p).toPromise();
						if (res?.success && res.memo) {
							if (res.open_invoices_by_customer) {
								this.openInvoicesByCustomer = { ...this.openInvoicesByCustomer, ...res.open_invoices_by_customer };
							}
							this.enqueueOneMemo(res.memo, file.name, p);
							job.done++;
						} else {
							job.failed++; job.lastError = res?.message || ('Page ' + p + ' failed');
						}
					} catch (e: any) {
						job.failed++; job.lastError = e?.error?.message || e?.message || ('Page ' + p + ' errored');
					}
				}
			}
			this.globalSearchService.showNotification(
				`${file.name}: ${job.done} memo(s) read` + (job.failed ? ` · ${job.failed} failed — ${job.lastError || ''}` : ''),
				job.failed ? 'warning' : 'success', 'bottom', 'left'
			);
		} catch {
			this.globalSearchService.showNotification(`${file.name}: could not read file`, 'danger', 'bottom', 'left');
		} finally {
			this.processing--;
			if (job && job.failed === 0) { const j = job; this.cmJobs = this.cmJobs.filter(x => x !== j); }
		}
	}

	/** Remembered preference, else ask once for multi-page PDFs. */
	private async resolveGroupMode(file: File): Promise<GroupMode | null> {
		const remembered = this.documentIntakeService.getSplitPref();
		if (remembered) { return remembered; }
		const pages = await this.documentIntakeService.getPdfPageCount(file);
		if (pages <= 1) { return 'single'; }
		const ref = this.dialog.open(MultipagePromptComponent, {
			width: '480px',
			data: { fileName: file.name, pageCount: pages },
		});
		const result = await ref.afterClosed().toPromise() as MultipagePromptResult | null;
		if (!result) { return null; }
		if (result.remember) { this.documentIntakeService.setSplitPref(result.mode); }
		return result.mode;
	}

	/**
	 * Spreadsheet import (CSV or Excel — app-excel-mapper parses both server-side and emits
	 * rows keyed by our destination columns). Memos files are line-level (one row per item,
	 * grouped by Memo No, with a COMMISSION line and a repeated Credit Total), so group +
	 * aggregate into memo objects, then match via the same engine as the PDF path.
	 */
	onRowsMapped(rows: any[]): void {
		if (!Array.isArray(rows) || !rows.length) { return; }
		const num = (v: any) => {
			const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.\-]/g, ''));
			return isNaN(n) ? 0 : Math.abs(n);
		};
		const isCommission = (d: any) => /commission/i.test(String(d || ''));

		const groups = new Map<string, any>();
		for (const r of rows) {
			const key = String(r.memo_no || '').trim();
			if (!key) { continue; }
			if (!groups.has(key)) {
				groups.set(key, {
					document_type: 'credit_memo',
					credit_memo_number: key,
					vendor_name: String(r.vendor_name || '').trim(),
					customer_name: String(r.customer_name || '').trim(),
					customer_account: String(r.customer_account || '').trim(),
					customer_ship_to: '',
					source_invoice_ref: String(r.invoice_ref || '').trim(),
					po_number: String(r.invoice_ref || '').trim(),
					invoice_date: String(r.memo_date || '').trim(),
					due_date: '',
					total_amount: 0,
					commission_amount: 0,
					relief_amount: 0,
					lines: [],
				});
			}
			const g = groups.get(key);
			// Credit Total repeats on every line — take the largest seen as the memo total.
			g.total_amount = Math.max(g.total_amount, num(r.total_amount));
			if (!g.vendor_name && r.vendor_name) { g.vendor_name = String(r.vendor_name).trim(); }
			// Commission can arrive two ways: its own column (one row per memo) or a COMMISSION line.
			if (r.commission_amount != null && r.commission_amount !== '') {
				g.commission_amount = Math.max(g.commission_amount, num(r.commission_amount));
			}
			const ext = num(r.line_extended);
			if (isCommission(r.description)) {
				g.commission_amount += ext;
			} else if (r.description || ext) {
				g.lines.push({ itemcode: '', description: String(r.description || ''), qty: 0, unit_price: 0, extended: ext });
			}
		}

		const memos = Array.from(groups.values()).map(g => {
			g.commission_amount = Math.round(g.commission_amount * 100) / 100;
			g.relief_amount = Math.round((g.total_amount - g.commission_amount) * 100) / 100;
			return g;
		});
		if (!memos.length) {
			this.globalSearchService.showNotification('No memos found in the file (check the Memo No mapping)', 'warning', 'bottom', 'left');
			return;
		}

		this.matchInBatches(memos);
	}

	/** Match memos to customers/invoices in chunks so a large file doesn't go in one slow
	 *  request — the queue fills as each batch returns, with a live counter. */
	private async matchInBatches(memos: any[]): Promise<void> {
		const vendorId = this.fileVendor?.supplierid;
		const customer = this.fileCustomer || undefined;
		const chunk = CreditMemoIntakeComponent.MATCH_CHUNK;
		this.matching = true;
		this.matchedCount = 0;
		this.matchTotal = memos.length;
		let failed = 0;
		try {
			for (let i = 0; i < memos.length; i += chunk) {
				const batch = memos.slice(i, i + chunk);
				try {
					const res: any = await this.documentIntakeService.matchMemos(batch, vendorId, customer).toPromise();
					if (res?.success && res.memos?.length) {
						this.enqueueMemos(res, 'spreadsheet');
					} else {
						failed += batch.length;
					}
				} catch {
					failed += batch.length;
				}
				this.matchedCount = Math.min(this.matchTotal, i + batch.length);
			}
		} finally {
			this.matching = false;
		}
		this.globalSearchService.showNotification(
			`Matched ${this.matchTotal - failed} of ${this.matchTotal} memo(s)` + (failed ? ` · ${failed} failed` : ''),
			failed ? 'warning' : 'success', 'bottom', 'left'
		);
	}

	/** Hand the memo to the payment screen for its matched customer and review there. */
	openInPayment(item: QueueItem): void {
		const cm = item.memo.customer_match;
		if (!cm || !cm.debtorno) {
			this.globalSearchService.showNotification('No confident customer match — open the customer manually, then drop this PDF on the payment form', 'warning', 'bottom', 'left');
			return;
		}
		this.documentIntakeService.stashMemo(item.memo);
		this.router.navigate(['/receivables/payment-entry', cm.debtorno, cm.branchcode || '']);
	}

	/** Resolve the payment defaults needed to post without the interactive form. */
	private resolveDefaults(): NaPaymentDefaults | null {
		const naCfg = this.config?.nationalAccount;
		const method = this.paymentMethods[0];
		if (!naCfg?.reliefGl || !this.company?.currencydefault || !method) {
			return null;
		}
		return {
			bankaccount: resolveDefaultBankAccountCode(this.bankaccounts, this.company?.defaultBankAccount),
			currency: this.company.currencydefault,
			payment_type: method.paymentid,
			journalno: '',
			reliefGl: String(naCfg.reliefGl).trim(),
			commissionGl: String(naCfg.commissionGl || '').trim(),
		};
	}

	dryRunAll(): Promise<void> { return this.runReady(true); }
	postAllReady(): Promise<void> { return this.runReady(false); }

	/**
	 * Run every strictly-ready row through the real National Account post path, sequentially,
	 * with a live counter. dryRun=true makes the server validate + allocate then ROLL BACK
	 * (nothing written) so the operator can confirm 100% before committing. Failures continue.
	 */
	async runReady(dryRun: boolean): Promise<void> {
		if (this.posting) { return; }
		const defaults = this.resolveDefaults();
		if (!defaults) {
			this.globalSearchService.showNotification('Missing payment defaults (bank/currency/method) or National Account GL config — cannot run', 'danger', 'bottom', 'left');
			return;
		}
		// Resolve an open journal for the default method so commission/expense GL gets a tag.
		try {
			const methodName = this.paymentMethods[0]?.paymentname || '';
			const journals: any = await this.paymentsService.getJournals({ type: methodName }).toPromise();
			if (Array.isArray(journals) && journals.length) { defaults.journalno = journals[0]; }
		} catch { /* run still attempts; server surfaces a department/journal error per row */ }

		const targets = this.queue.filter(q => q.status === 'pending' && this.isReady(q));
		if (!targets.length) {
			this.globalSearchService.showNotification('No ready rows to run', 'info', 'bottom', 'left');
			return;
		}
		this.posting = true;
		this.runMode = dryRun ? 'dry' : 'live';
		this.postedCount = 0;
		this.failedCount = 0;
		this.postTotal = targets.length;

		for (const item of targets) {
			const payload = this.documentIntakeService.buildNaPayment(item.memo, defaults, this.computeToRows(item), dryRun);
			if (!payload) {
				if (dryRun) { item.dry = 'fail'; item.dryMessage = 'Could not build payment'; }
				else { item.status = 'skipped'; item.message = 'Could not build payment'; }
				this.failedCount++;
				continue;
			}
			if (!dryRun) { item.status = 'posting'; }
			try {
				const res: any = await this.paymentsService.nationalAccountPayment(payload).toPromise();
				if (res?.success) {
					if (dryRun) { item.dry = 'pass'; item.dryMessage = 'Preview passed'; }
					else { item.status = 'posted'; item.message = res.reference_id || ''; item.dry = undefined; item.duplicate = false; }
					this.postedCount++;
				} else {
					if (dryRun) { item.dry = 'fail'; item.dryMessage = res?.message || 'Preview failed'; }
					else { item.status = 'failed'; item.message = res?.message || 'Post failed'; }
					this.failedCount++;
				}
			} catch (e: any) {
				if (dryRun) { item.dry = 'fail'; item.dryMessage = 'Server error'; }
				else { item.status = 'failed'; item.message = 'Server error'; }
				this.failedCount++;
			}
		}

		this.posting = false;
		const verb = dryRun ? 'Preview' : 'Bulk post';
		this.globalSearchService.showNotification(
			`${verb} done: ${this.postedCount} ${dryRun ? 'previewed' : 'posted'}, ${this.failedCount} ${dryRun ? 'need review' : 'failed'}`,
			this.failedCount ? 'warning' : 'success', 'bottom', 'left'
		);
	}

	remove(item: QueueItem): void {
		this.queue = this.queue.filter(q => q.id !== item.id);
	}

	clearQueue(): void {
		this.queue = this.queue.filter(q => q.status === 'posting');
	}

	confidenceClass(score: number): string {
		if (score >= 0.6) { return 'cmi-badge--good'; }
		if (score > 0) { return 'cmi-badge--weak'; }
		return 'cmi-badge--none';
	}

	// ══════════════════════════════════════════════════════════════════════════
	//  VENDOR INVOICES (purchase invoices → GRN-matched AP entry)
	// ══════════════════════════════════════════════════════════════════════════
	vinvQueue: VendorInvoiceItem[] = [];
	vinvProcessing = 0;
	private vinvSeq = 0;
	private static readonly VINV_STORE_KEY = 'vendorintake.queue.v1';
	vinvPosting = false;
	vinvRunMode: 'dry' | 'live' | null = null;
	vinvPosted = 0;
	vinvFailed = 0;
	vinvTotal = 0;
	private vendorCache: { [supplierid: string]: any } = {};
	private grnPoolCache: { [supplierid: string]: any[] } = {};
	/** Raw base64 of each dropped PDF, kept in memory so the operator can eyeball the source page
	 *  against the extraction. Keyed by file name. NOT persisted to localStorage (PDFs are large) —
	 *  so the source view is available this session only; after a reload the row's data survives but
	 *  the "View source" button hides until the file is re-dropped. */
	vinvPdfSrc: { [fileName: string]: string } = {};
	/** Bound by the source-PDF modal template while open. */
	vinvSource: { src: string; title: string; page: number } | null = null;
	/** The source-PDF modal template, declared at the component root (out of the *ngFor scope). */
	@ViewChild('vinvSourceModal') private vinvSourceModalTpl?: TemplateRef<any>;

	vinvHasSource(item: VendorInvoiceItem): boolean {
		return !!(item.fileName && this.vinvPdfSrc[item.fileName]);
	}

	/** Open the dropped PDF at this invoice's page in a modal viewer, so the operator can read the
	 *  actual scan against the extraction. Rendered in a modal (not inline) because the pdf viewer
	 *  needs a sized container — inline in the card it collapses to zero height. */
	vinvViewSource(item: VendorInvoiceItem): void {
		const src = item.fileName ? this.vinvPdfSrc[item.fileName] : '';
		if (!src || !this.vinvSourceModalTpl) { return; }
		this.vinvSource = {
			src,
			title: item.fileName + (item.page ? ' · page ' + item.page : ''),
			page: item.page || 1,
		};
		this.modalService.open(this.vinvSourceModalTpl, { size: 'xl', ariaLabelledBy: 'vinv-source-title', windowClass: 'vinv-source-modal' });
	}

	/** Send/journal status for the per-row checkbox. 'sent' once posted; 'fail' on a failed post;
	 *  'dup' for an already-entered/in-batch duplicate; 'warn' for anything needing a human; 'ready'
	 *  when clean and postable; 'posting' mid-send; else 'pending' (not processed yet). */
	vinvSendState(item: VendorInvoiceItem): 'posting' | 'sent' | 'fail' | 'dup' | 'warn' | 'ready' | 'pending' {
		if (item.status === 'posting') { return 'posting'; }
		if (item.status === 'posted') { return 'sent'; }
		if (item.status === 'failed') { return 'fail'; }
		if (item.duplicate || this.vinvInBatchDup(item)) { return 'dup'; }
		if (this.vinvState(item) === 'exception') { return 'warn'; }
		if (this.vinvState(item) === 'ready') { return 'ready'; }
		return 'pending';
	}

	/** Material icon for the send checkbox, by state. */
	vinvSendIcon(item: VendorInvoiceItem): string {
		switch (this.vinvSendState(item)) {
			case 'posting': return 'hourglass_top';
			case 'sent':    return 'check_circle';
			case 'fail':    return 'cancel';
			case 'dup':     return 'content_copy';
			case 'warn':    return 'error';
			case 'ready':   return 'check_circle_outline';
			default:        return 'radio_button_unchecked';
		}
	}

	/** Tooltip for the send checkbox — the status, plus a hint that clicking opens the scanned page. */
	vinvSendTitle(item: VendorInvoiceItem): string {
		const s = this.vinvSendState(item);
		const label = s === 'sent' ? ('Sent ' + (item.message || '')).trim()
			: s === 'fail' ? ('Failed' + (item.message ? ' — ' + item.message : ''))
			: s === 'posting' ? 'Sending…'
			: this.vinvReviewReason(item);
		return this.vinvHasSource(item) ? label + ' · click to view the scanned page' : label;
	}

	private isTires(): boolean {
		return this.config?.env?.package === 'tires';
	}

	/** Save the invoice scratchpad to localStorage so a reload/nav doesn't re-hit the AI. */
	persist(): void {
		try {
			const slim = this.vinvQueue.map(q => ({
				id: q.id, fileName: q.fileName, page: q.page, pages: q.pages, fields: q.fields,
				vendor_match: q.vendor_match, vendor: q.vendor, grns: q.grns,
				matchedCodes: q.matchedCodes, unmatched: q.unmatched,
				status: q.status === 'posting' ? 'pending' : q.status, message: q.message,
				dry: q.dry, dryMessage: q.dryMessage, glPreview: q.glPreview,
				duplicate: q.duplicate, dupPosted: q.dupPosted, dupTransno: q.dupTransno, forcePost: q.forcePost,
				expanded: q.expanded, showRaw: q.showRaw,
			}));
			localStorage.setItem(CreditMemoIntakeComponent.VINV_STORE_KEY, JSON.stringify(slim));
		} catch { /* quota / serialization — non-fatal, scratchpad just won't persist this change */ }
	}

	/** Restore the scratchpad on load, then re-warm each vendor's GRN pool (no AI, no re-match). */
	private restoreVinvQueue(): void {
		let saved: any[] = [];
		try {
			const raw = localStorage.getItem(CreditMemoIntakeComponent.VINV_STORE_KEY);
			saved = raw ? JSON.parse(raw) : [];
		} catch { saved = []; }
		if (!Array.isArray(saved) || !saved.length) { return; }
		this.vinvQueue = saved.map((q: any) => ({
			id: q.id, fileName: q.fileName, page: q.page, pages: q.pages, fields: q.fields,
			vendor_match: q.vendor_match || null, vendor: q.vendor || null, grns: q.grns || [],
			matchedCodes: q.matchedCodes || [], unmatched: q.unmatched || [],
			loadingVendor: false, status: q.status === 'posting' ? 'pending' : (q.status || 'pending'),
				message: q.message || '', dry: q.dry, dryMessage: q.dryMessage, glPreview: q.glPreview,
				duplicate: q.duplicate, dupPosted: q.dupPosted, dupTransno: q.dupTransno, forcePost: q.forcePost,
				expanded: q.expanded, showRaw: false,
		}) as VendorInvoiceItem);
		this.vinvSeq = this.vinvQueue.reduce((m, q) => Math.max(m, q.id || 0), 0);
		this.warmVinvPools();
		// Re-verify duplicates against current data (an invoice may have been posted since saving).
		this.vinvQueue.forEach(item => this.checkDuplicate(item));
	}

	/** Fetch the open-GRN pool for each distinct vendor in the restored queue (so add/remove works). */
	private warmVinvPools(): void {
		const ids = Array.from(new Set(this.vinvQueue.map(q => q.vendor_match?.supplierid).filter(Boolean))) as string[];
		ids.forEach(sid => {
			if (this.grnPoolCache[sid]) { return; }
			this.purchasingService.getGrnDetails(sid).subscribe(
				(r: any) => { this.grnPoolCache[sid] = Array.isArray(r) ? r : []; },
				() => { /* pool warm is best-effort; matched lines already persisted */ }
			);
		});
	}

	onVendorPdfsDropped(files: FileList | File[] | null): void {
		const list = files ? Array.from(files as any) as File[] : [];
		const pdfs = list.filter(f => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
		if (!pdfs.length) {
			this.globalSearchService.showNotification('Drop one or more PDF vendor invoices', 'warning', 'bottom', 'left');
			return;
		}
		pdfs.forEach(f => this.processVendorFile(f));
	}

	/**
	 * Stage a (possibly multi-invoice) PDF for reading: count pages, keep the bytes, then enqueue
	 * EVERY page as a `queued` step up front and persist that job BEFORE any AI call. So if the AI
	 * dies mid-batch (credits run out, network drop), the unread pages survive a reload and each
	 * failed/queued page gets a Resend button — nothing in the scratchpad is lost. Then drain the
	 * pages one at a time (the PDF is prompt-cached server-side, so pages 2..N are cheap).
	 */
	private async processVendorFile(file: File): Promise<void> {
		let job: VinvLoadJob | null = null;
		try {
			const pages = await this.documentIntakeService.getPdfPageCount(file);
			const total = Math.max(1, pages || 1);
			const b64 = await this.documentIntakeService.fileToBase64(file);
			this.vinvPdfSrc[file.name] = b64;  // retain for the in-screen source-page viewer + resend
			job = {
				id: ++this.vinvJobSeq, fileName: file.name, total, current: 0, done: 0, failed: 0,
				steps: Array.from({ length: total }, () => 'queued' as VinvPageState),
			};
			this.vinvJobs.push(job);
			this.persistVinvJobs();
			this.persistVinvPdf();
			await this.drainVinvJob(job);
		} catch {
			this.globalSearchService.showNotification(`${file.name}: could not read file`, 'danger', 'bottom', 'left');
		}
	}

	/** Read every queued/failed page of a job, one at a time, persisting after each so progress survives. */
	private async drainVinvJob(job: VinvLoadJob): Promise<void> {
		const b64 = this.vinvPdfSrc[job.fileName];
		if (!b64) {
			this.globalSearchService.showNotification(
				`${job.fileName}: re-drop the PDF to resend (source not loaded this session)`, 'warning', 'bottom', 'left');
			return;
		}
		if (job.running) { return; }
		job.running = true;
		this.vinvProcessing++;
		try {
			for (let p = 1; p <= job.total; p++) {
				if (job.steps[p - 1] === 'done') { continue; }   // already read — don't re-bill the AI
				job.current = p;
				job.steps[p - 1] = 'reading';
				this.persistVinvJobs();
				try {
					const res: any = await this.documentIntakeService.extractVendorInvoicePage(b64, p).toPromise();
					if (res?.success && res.invoice) {
						this.enqueueVendorInvoice({ fields: res.invoice, vendor_match: res.vendor_match || null }, job.fileName, p);
						job.steps[p - 1] = 'done';
					} else {
						job.steps[p - 1] = 'failed';
						job.lastError = res?.message || ('Page ' + p + ' failed');
					}
				} catch (e: any) {
					job.steps[p - 1] = 'failed';
					job.lastError = e?.error?.message || e?.message || ('Page ' + p + ' errored');
				}
				this.recountVinvJob(job);
				this.persistVinvJobs();
			}
			this.globalSearchService.showNotification(
				`${job.fileName}: ${job.done} invoice(s) read` + (job.failed ? ` · ${job.failed} failed — ${job.lastError || ''}` : ''),
				job.failed ? 'warning' : 'success', 'bottom', 'left'
			);
		} finally {
			this.vinvProcessing--;
			job.current = 0;
			job.running = false;
			// Clear the stepper only when every page is read; keep it (with Resend) while any page is open.
			if (job.failed === 0 && job.done === job.total) {
				this.vinvJobs = this.vinvJobs.filter(j => j !== job);
			}
			this.persistVinvJobs();
		}
	}

	/** Re-read just the failed/queued pages of a job (e.g. after topping up AI credits). */
	resendVinvJob(job: VinvLoadJob): void {
		if (job.running) { return; }
		job.lastError = undefined;
		this.drainVinvJob(job);
	}

	private recountVinvJob(job: VinvLoadJob): void {
		job.done = job.steps.filter(s => s === 'done').length;
		job.failed = job.steps.filter(s => s === 'failed').length;
	}

	/** True while a job has any page still queued or failed (so the Resend button shows). */
	vinvJobHasOpen(job: VinvLoadJob): boolean {
		return job.steps.some(s => s === 'queued' || s === 'failed');
	}

	dismissVinvJob(job: VinvLoadJob): void {
		this.vinvJobs = this.vinvJobs.filter(j => j !== job);
		this.persistVinvJobs();
	}

	/** Loading-stepper state for an in-flight file (one step per page). */
	vinvJobs: VinvLoadJob[] = [];
	private vinvJobSeq = 0;
	private static readonly VINV_JOBS_KEY = 'vendorintake.jobs.v1';
	private static readonly VINV_PDF_KEY = 'vendorintake.pdfs.v1';

	/** Stepper dot state, read straight from the persisted per-page status. */
	vinvStepState(job: VinvLoadJob, pageNum: number): 'done' | 'active' | 'pending' | 'failed' {
		const s = job.steps[pageNum - 1];
		if (s === 'done') { return 'done'; }
		if (s === 'reading') { return 'active'; }
		if (s === 'failed') { return 'failed'; }
		return 'pending';
	}
	vinvStepArray(job: VinvLoadJob): number[] {
		return Array.from({ length: job.total }, (_, i) => i + 1);
	}

	/** Persist the page-read jobs (small: per-page status, no bytes) so a reload keeps the batch. */
	private persistVinvJobs(): void {
		try {
			const slim = this.vinvJobs.map(j => ({
				id: j.id, fileName: j.fileName, total: j.total, current: 0,
				done: j.done, failed: j.failed, lastError: j.lastError,
				steps: j.steps.map(s => (s === 'reading' ? 'queued' : s)),  // a reload aborts any in-flight read
			}));
			localStorage.setItem(CreditMemoIntakeComponent.VINV_JOBS_KEY, JSON.stringify(slim));
		} catch { /* quota — non-fatal */ }
	}

	/** Best-effort persist of the PDF bytes so Resend works even after a reload. Skipped silently on quota. */
	private persistVinvPdf(): void {
		try {
			localStorage.setItem(CreditMemoIntakeComponent.VINV_PDF_KEY, JSON.stringify(this.vinvPdfSrc));
		} catch { /* PDFs can blow the quota — fall back to in-session-only; Resend prompts a re-drop */ }
	}

	/** Restore staged page-jobs (and any persisted PDF bytes) on load; do NOT auto-resend. */
	private restoreVinvJobs(): void {
		try {
			const rawPdf = localStorage.getItem(CreditMemoIntakeComponent.VINV_PDF_KEY);
			const pdfs = rawPdf ? JSON.parse(rawPdf) : null;
			if (pdfs && typeof pdfs === 'object') { this.vinvPdfSrc = { ...this.vinvPdfSrc, ...pdfs }; }
		} catch { /* ignore */ }
		let saved: any[] = [];
		try {
			const raw = localStorage.getItem(CreditMemoIntakeComponent.VINV_JOBS_KEY);
			saved = raw ? JSON.parse(raw) : [];
		} catch { saved = []; }
		if (!Array.isArray(saved) || !saved.length) { return; }
		this.vinvJobs = saved.map((j: any) => {
			const total = Math.max(1, j.total || (Array.isArray(j.steps) ? j.steps.length : 1));
			const steps: VinvPageState[] = Array.isArray(j.steps) && j.steps.length === total
				? j.steps.map((s: any) => (s === 'done' || s === 'failed' ? s : 'queued'))
				: Array.from({ length: total }, () => 'queued' as VinvPageState);
			const job: VinvLoadJob = {
				id: j.id || 0, fileName: j.fileName || '', total, current: 0,
				done: 0, failed: 0, lastError: j.lastError, steps,
			};
			this.recountVinvJob(job);
			return job;
		}).filter(j => this.vinvJobHasOpen(j));   // drop fully-read jobs; only keep ones with work left
		this.vinvJobSeq = this.vinvJobs.reduce((m, j) => Math.max(m, j.id || 0), 0);
	}

	private enqueueVendorInvoice(c: VendorInvoiceCandidate, fileName: string, page?: number): void {
		const incoming = c.fields;
		const ref = (incoming?.invoice_number || '').trim().toUpperCase();
		// Multi-page handling. Pages of ONE invoice arrive as separate per-page reads. If a row with
		// the same invoice # from the same file is already queued, decide combine-vs-duplicate by
		// CONTENT (we do our own dedup rather than trusting the AI's page grouping):
		//   • same PO + same lines → the scanner fed the same page twice → drop it as a double scan
		//   • different lines       → a continuation page → combine its lines into the one invoice
		if (ref) {
			const existing = this.vinvQueue.find(q => q.fileName === fileName
				&& (q.fields.invoice_number || '').trim().toUpperCase() === ref);
			if (existing) {
				if (this.sameInvoiceContent(existing.fields, incoming)) {
					this.globalSearchService.showNotification(
						`Duplicate scan ignored — invoice ${incoming.invoice_number} already read`, 'info', 'bottom', 'left');
				} else {
					this.mergeVendorInvoicePage(existing, incoming, page);
					this.globalSearchService.showNotification(
						`Combined another page into invoice ${incoming.invoice_number}`, 'info', 'bottom', 'left');
				}
				return;
			}
		}
		const item: VendorInvoiceItem = {
			id: ++this.vinvSeq,
			fileName,
			page: page != null ? page : c.fields?.page,
			fields: c.fields,
			vendor_match: c.vendor_match,
			vendor: null,
			grns: [],
			matchedCodes: [],
			unmatched: [],
			loadingVendor: false,
			status: 'pending',
			message: '',
		};
		this.vinvQueue.push(item);
		this.persist();
		if (c.vendor_match?.supplierid && c.vendor_match.auto) {
			this.loadVendorAndMatch(item);
		}
	}

	/** Order-independent signature of an invoice's lines (item + qty + price) — for deciding whether
	 *  two scanned pages carry the SAME content (a double scan) vs different lines (a continuation). */
	private vinvLineSig(fields: VendorInvoiceFields): string {
		const lines = Array.isArray(fields?.lines) ? fields.lines : [];
		const n = (s: any) => this.documentIntakeService.normCode(s);
		return lines
			.map(l => `${n(l.itemcode)}|${n(l.description)}|${Number(l.qty) || 0}|${Number(l.unit_price) || 0}`)
			.sort()
			.join('~');
	}

	/** Order-independent signature of the invoice's PO numbers. */
	private vinvPoSig(fields: VendorInvoiceFields): string {
		return (fields?.po_numbers || []).map(p => this.documentIntakeService.normCode(p)).filter(Boolean).sort().join(',');
	}

	/** Two reads are the SAME invoice when their PO set AND line set match — the same page scanned
	 *  twice, not a continuation page that adds new lines. */
	private sameInvoiceContent(a: VendorInvoiceFields, b: VendorInvoiceFields): boolean {
		return this.vinvPoSig(a) === this.vinvPoSig(b) && this.vinvLineSig(a) === this.vinvLineSig(b);
	}

	/** Fold a continuation page into an existing invoice: append its NEW lines, union the PO numbers,
	 *  adopt the larger header money (the grand total prints on one page), track the spanned pages,
	 *  then re-suggest GRNs and re-run the duplicate guard against the combined line set. */
	private mergeVendorInvoicePage(item: VendorInvoiceItem, incoming: VendorInvoiceFields, page?: number): void {
		const f = item.fields;
		const n = (s: any) => this.documentIntakeService.normCode(s);
		const lineKey = (l: any) => `${n(l.itemcode)}|${n(l.description)}|${Number(l.qty) || 0}|${Number(l.unit_price) || 0}`;
		const seen = new Set((f.lines || []).map(lineKey));
		(incoming.lines || []).forEach(l => { if (!seen.has(lineKey(l))) { (f.lines = f.lines || []).push(l); seen.add(lineKey(l)); } });
		// Union PO numbers — a continuation page can carry a PO the first page didn't.
		const pos = new Set((f.po_numbers || []).map(p => String(p).trim()).filter(Boolean));
		(incoming.po_numbers || []).forEach(p => { const s = String(p).trim(); if (s) { pos.add(s); } });
		f.po_numbers = Array.from(pos);
		// Header money: the grand total prints once (on the page with the totals) → take the larger.
		f.total_amount = Math.max(Number(f.total_amount) || 0, Number(incoming.total_amount) || 0);
		f.subtotal = Math.max(Number(f.subtotal) || 0, Number(incoming.subtotal) || 0);
		f.tax_amount = Math.max(Number(f.tax_amount) || 0, Number(incoming.tax_amount) || 0);
		f.freight_amount = Math.max(Number(f.freight_amount) || 0, Number(incoming.freight_amount) || 0);
		f.fet_amount = Math.max(Number(f.fet_amount) || 0, Number(incoming.fet_amount) || 0);
		if (!(Number(f.discount_amount) > 0) && Number(incoming.discount_amount) > 0) { f.discount_amount = incoming.discount_amount; }
		// Track the source pages this card now spans (for the "p1, p2" hint).
		if (page != null) {
			const ps = new Set<number>(item.pages || (item.page != null ? [item.page] : []));
			ps.add(page);
			item.pages = Array.from(ps).sort((a, b) => a - b);
		}
		// Re-suggest GRNs for the now-complete line set if the vendor pool is loaded; else it matches on load.
		const sid = item.vendor_match?.supplierid;
		if (sid && this.grnPoolCache[sid]) {
			const m = this.documentIntakeService.matchInvoiceToGrns(item.fields, this.grnPoolCache[sid]);
			item.grns = m.grns; item.matchedCodes = m.matchedCodes; item.unmatched = m.unmatched;
		}
		item.dry = undefined; item.dryMessage = '';
		this.checkDuplicate(item);
		this.persist();
	}

	/** Load the matched vendor's full record + open GRN pool (cached per supplier), then suggest GRNs. */
	private loadVendorAndMatch(item: VendorInvoiceItem): void {
		const supplierid = item.vendor_match?.supplierid;
		if (!supplierid || item.loadingVendor) { return; }
		item.loadingVendor = true;

		const needVendor = !this.vendorCache[supplierid];
		const needGrns = !this.grnPoolCache[supplierid];
		const vendor$ = needVendor ? this.vendorService.getVendor(supplierid) : of(this.vendorCache[supplierid]);
		const grns$ = needGrns ? this.purchasingService.getGrnDetails(supplierid) : of(this.grnPoolCache[supplierid]);

		forkJoin({ vendor: vendor$, grns: grns$ }).subscribe(
			(r: any) => {
				item.loadingVendor = false;
				this.vendorCache[supplierid] = r.vendor;
				this.grnPoolCache[supplierid] = Array.isArray(r.grns) ? r.grns : [];
				item.vendor = r.vendor;
				this.fillDueDateFromTerms(item.fields, r.vendor);
				const m = this.documentIntakeService.matchInvoiceToGrns(item.fields, this.grnPoolCache[supplierid]);
				item.grns = m.grns;
				item.matchedCodes = m.matchedCodes;
				item.unmatched = m.unmatched;
				this.checkDuplicate(item);
				this.persist();
			},
			() => {
				item.loadingVendor = false;
				this.globalSearchService.showNotification('Could not load vendor / GRNs', 'danger', 'bottom', 'left');
			}
		);
	}

	/** Derive the due date from the vendor's payment terms (invoice date + days-before-due /
	 *  day-in-following-month / statement periods), the same computation the AP entry screens use.
	 *  Distributor invoices (e.g. ATD) print no due date and the AI can echo or guess one, so we
	 *  never trust the read due date — we always compute it ourselves once the vendor is known.
	 *  The operator can still override the due date per-invoice on the card. */
	private fillDueDateFromTerms(fields: VendorInvoiceFields, vendor: any): void {
		const inv = (fields.invoice_date || '').trim();
		if (!inv || !vendor) { return; }
		this.vendorService.computeDueDate({
			invoiceDate: inv,
			daysbeforedue: parseInt(vendor.daysbeforedue, 10) || 0,
			dayinfollowingmonth: parseInt(vendor.dayinfollowingmonth, 10) || 0,
			statementperiods: parseInt(vendor.statementperiods, 10) || 0,
		}).subscribe((r: any) => {
			if (!r || !r.duedate) { return; }
			const d = new Date(r.duedate);
			if (isNaN(d.getTime())) { return; }
			const p = (n: number) => String(n).padStart(2, '0');
			fields.due_date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
			this.persist();
		});
	}

	/** Operator confirms the suggested vendor manually (when the name match wasn't confident). */
	confirmVendor(item: VendorInvoiceItem, v: any): void {
		const supplierid = v?.supplierid || v?.supplier_id || v?.id || '';
		if (!supplierid) { return; }
		item.vendor_match = { supplierid, suppname: v?.suppname || v?.name || String(supplierid), currcode: v?.currcode || '', score: 1, auto: true };
		item.dry = undefined; item.dryMessage = '';
		this.persist();
		this.loadVendorAndMatch(item);
	}

	vinvTotals(item: VendorInvoiceItem): { subtotal: number; taxes: number; fet: number; total: number } {
		if (!item.vendor) { return { subtotal: 0, taxes: 0, fet: 0, total: 0 }; }
		const clone = JSON.parse(JSON.stringify(item.vendor));
		return this.documentIntakeService.computeVendorInvoiceTotals(clone, item.grns, this.vinvEffectiveDiscount(item), this.isTires());
	}

	/**
	 * The discount we actually book. An AI-read discount is only deducted when the invoice's stated
	 * total ALREADY reflects it (a genuine trade discount baked into the total → take it, allocate it
	 * per line). When the goods already equal the stated total, the discount is an early-pay term
	 * (e.g. USAUTOFORCE via TermSync) that's taken at PAYMENT, not at entry — so we book the gross
	 * invoice and the screen reconciles. An ambiguous read keeps the discount (and will flag).
	 */
	vinvEffectiveDiscount(item: VendorInvoiceItem): number {
		const disc = Number(item.fields.discount_amount) || 0;
		if (disc <= 0) { return 0; }
		const stated = Number(item.fields.total_amount) || 0;
		if (stated <= 0 || !item.vendor) { return disc; }
		const clone = JSON.parse(JSON.stringify(item.vendor));
		const gross = this.documentIntakeService.computeVendorInvoiceTotals(clone, item.grns, 0, this.isTires()).total;
		if (Math.abs(gross - stated) <= 0.01) { return 0; }          // discount not in the total → early-pay term
		if (Math.abs(gross - disc - stated) <= 0.01) { return disc; } // discount in the total → trade discount
		return disc;                                                  // neither lines up → keep; the mismatch flag stands
	}

	/** What to show in the "Entered" column. For an already-entered invoice we show what was actually
	 *  POSTED (from the dup check), not $0.00 — the GRNs aren't matched on a duplicate so the live
	 *  total would read zero. */
	vinvEnteredDisplay(item: VendorInvoiceItem): number {
		if (item.duplicate && item.dupPosted != null) { return item.dupPosted; }
		return this.vinvTotals(item).total;
	}

	/** Entered total reconciles to the invoice's stated total (to the cent). */
	vinvReconciles(item: VendorInvoiceItem): boolean {
		const entered = this.vinvTotals(item).total;
		const stated = Number(item.fields.total_amount) || 0;
		// The AI sometimes reads `total_amount` as the merchandise SUBTOTAL, leaving freight/FET out of
		// it. Accept a match against the invoice's reconstructed total too: subtotal + tax + freight + FET.
		const reconstructed = (Number(item.fields.subtotal) || 0) + (Number(item.fields.tax_amount) || 0)
			+ (Number(item.fields.freight_amount) || 0) + (Number(item.fields.fet_amount) || 0);
		return (stated > 0 && Math.abs(entered - stated) <= 0.01)
			|| (reconstructed > 0 && Math.abs(entered - reconstructed) <= 0.01);
	}

	/** One-line summary of the GRN lines selected on an invoice — shown in the COLLAPSED header so the
	 *  operator sees what will post (qty × item, GRN/PO) without expanding the card. */
	vinvSelSummary(item: VendorInvoiceItem): string {
		const grns = item.grns || [];
		if (!grns.length) { return 'no lines yet'; }
		const fmt = (n: any) => { const v = Number(n) || 0; return v % 1 === 0 ? String(v) : v.toFixed(2); };
		const head = grns.slice(0, 3).map(g => `${fmt(g.toinvoice)}× ${g.itemcode}`).join(', ');
		const more = grns.length > 3 ? ` +${grns.length - 3} more` : '';
		const pos = Array.from(new Set(grns.map(g => g.orderno).filter(Boolean)));
		const poStr = pos.length ? ` · PO ${pos.slice(0, 3).join(', ')}${pos.length > 3 ? '…' : ''}` : '';
		return `${grns.length} line${grns.length > 1 ? 's' : ''}: ${head}${more}${poStr}`;
	}

	/** Collapsed-header summary for a credit: which AR invoices it applies to and how much. */
	cmSelSummary(item: QueueItem): string {
		const rows = this.computeToRows(item);
		if (!rows.length) { return 'no invoices selected'; }
		const head = rows.slice(0, 3).map(r => `#${r.transno} ${this.cmMoney(r.allocation)}`).join(', ');
		const more = rows.length > 3 ? ` +${rows.length - 3} more` : '';
		return `applying ${this.cmMoney(this.allocatedTotal(item))} to ${rows.length} invoice${rows.length > 1 ? 's' : ''}: ${head}${more}`;
	}

	private cmMoney(n: number): string {
		return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
	}

	/** True when the vendor's invoice price on a line differs from our PO/receipt price (posts a PPV). */
	vinvPriceDiffers(grn: any): boolean {
		const po = parseFloat(grn?.poUnitPrice);
		const inv = parseFloat(grn?.unitprice);
		if (!Number.isFinite(po) || !Number.isFinite(inv)) { return false; }
		return Math.abs(po - inv) > 0.005;
	}

	/** Accept the vendor's invoice price as the real price: correct the PO line (our price was wrong)
	 *  so the order record + future receipts use it, and match "our price" to it on screen. */
	vinvAcceptPrice(item: VendorInvoiceItem, grn: any): void {
		const price = parseFloat(grn?.unitprice) || 0;
		if (!grn?.podetailitem) {
			// No PO line behind it (manual/no-PO line) — just match locally.
			grn.poUnitPrice = price;
			this.persist();
			return;
		}
		this.vendorService.updatePoLinePrice(grn.podetailitem, price).subscribe(
			(r: any) => {
				if (r && r.success) {
					grn.poUnitPrice = price;
					this.persist();
					this.globalSearchService.showNotification('PO price updated to ' + price.toFixed(2) + ' for ' + (grn.itemcode || 'line'), 'success', 'bottom', 'left');
				} else {
					this.globalSearchService.showNotification((r && r.message) || 'Could not update the PO price', 'danger', 'bottom', 'left');
				}
			},
			() => this.globalSearchService.showNotification('Could not update the PO price', 'danger', 'bottom', 'left')
		);
	}

	/** Per-line FET = fetcharge × qty (tires only). 0 when not a tires package or no FET on the line. */
	vinvLineFet(grn: any): number {
		if (!this.isTires()) { return 0; }
		return (parseFloat(grn?.fetcharge) || 0) * (parseFloat(grn?.toinvoice) || 0);
	}

	/** Difference between what we'll post (entered) and the invoice's stated total — the FET/freight
	 *  gap usually. Shown so the operator can see and understand the delta. */
	vinvDiff(item: VendorInvoiceItem): number {
		return Math.round((this.vinvTotals(item).total - (Number(item.fields.total_amount) || 0)) * 100) / 100;
	}

	/** The invoice's extracted extended amount for the item on a GRN line (summed across split lines),
	 *  or null when the invoice has no matching line — for spotting the line that's actually off. */
	vinvInvExtForLine(item: VendorInvoiceItem, g: any): number | null {
		const lines = (item.fields && item.fields.lines) ? item.fields.lines : [];
		if (!lines.length) { return null; }
		const gkeys = this.documentIntakeService.codeKeys(g.itemcode);
		if (!gkeys.length) { return null; }
		let sum = 0; let found = false;
		for (const l of lines) {
			const lk = [...this.documentIntakeService.codeKeys(l.itemcode), ...this.documentIntakeService.codeKeys(l.description)];
			if (lk.some(k => gkeys.indexOf(k) >= 0)) {
				// Compare on the MERCHANDISE basis (qty × unit_price) we adopt as the line price — not the
				// vendor's `extended`, which can fold in the line's FET and would falsely flag a tire line.
				const q = parseFloat(String(l.qty)) || 0;
				const p = parseFloat(String(l.unit_price)) || 0;
				sum += (p > 0) ? q * p : ((l.extended != null && (l.extended as any) !== '') ? (parseFloat(String(l.extended)) || 0) : 0);
				found = true;
			}
		}
		return found ? Math.round(sum * 100) / 100 : null;
	}

	/** A GRN line whose extended (our price × qty) doesn't match the invoice's line for that item —
	 *  this is the line to look at when totals differ. */
	vinvLineMismatch(item: VendorInvoiceItem, g: any): boolean {
		const inv = this.vinvInvExtForLine(item, g);
		if (inv == null) { return false; }
		const our = Math.round(((parseFloat(g.unitprice) || 0) * (parseFloat(g.toinvoice) || 0)) * 100) / 100;
		return Math.abs(our - inv) > 0.01;
	}

	/** Plain-language reason the entered total differs from the invoice — so the operator can tell
	 *  whether it's just the FET (safe to post) or a real line-price problem. */
	vinvMismatchExplain(item: VendorInvoiceItem): string {
		const d = this.vinvDiff(item);
		if (Math.abs(d) <= 0.01) { return ''; }
		const money = (n: number) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
		const fet = this.vinvTotals(item).fet;
		const badLines = (item.grns || []).filter(g => this.vinvLineMismatch(item, g)).map(g => g.itemcode);
		if (badLines.length) {
			return `Off by ${money(d)} — check ${badLines.length} line(s): ${badLines.slice(0, 3).join(', ')}${badLines.length > 3 ? '…' : ''}.`;
		}
		// Entered is over by exactly our computed FET → the invoice total already includes its FET, so
		// posting our total would overpay by that FET. Zero the line FET (or fix the price) to match.
		if (fet > 0 && Math.abs(d - fet) <= 0.01) {
			return `Over by ${money(d)} = our FET (${money(fet)}); the invoice total already includes FET. Zero the line FET to match.`;
		}
		return `${d > 0 ? 'Over' : 'Under'} by ${money(Math.abs(d))} — review the lines.`;
	}

	/** Duplicate-type issues render as a RED pill (matching the batch-journal flag), not orange. */
	vinvIssueIsDup(issue: string): boolean {
		return issue === 'Already entered' || issue === 'Double scan';
	}
	/** Same red-pill rule for the credit-memo cards. */
	cmIssueIsDup(issue: string): boolean {
		return issue === 'Already entered' || issue === 'Double scan';
	}

	vinvSetQty(item: VendorInvoiceItem, grn: any, ev: any): void {
		grn.toinvoice = parseFloat(ev?.target?.value) || 0;
		this.persist();
	}
	vinvSetPrice(item: VendorInvoiceItem, grn: any, ev: any): void {
		grn.unitprice = parseFloat(ev?.target?.value) || 0;
		this.persist();
	}
	vinvRemoveGrn(item: VendorInvoiceItem, grn: any): void {
		item.grns = item.grns.filter(g => g !== grn);
		this.persist();
	}

		/** Ready = confident vendor loaded, GRNs reconcile to the invoice total, an invoice reference, and NOT a duplicate. */
		vinvReady(item: VendorInvoiceItem): boolean {
			return !!item.vendor
				&& item.grns.length > 0
				&& !!(item.fields.invoice_number || '').trim()
				&& !item.duplicate
				&& !this.vinvInBatchDup(item)
				&& (this.vinvReconciles(item) || !!item.forcePost);
		}

		/**
		 * Triage each invoice so the screen hides what's fine and surfaces what isn't:
		 *  'posted' (done → fades down) · 'exception' (needs a human) · 'ready' (clean, postable).
		 */
		vinvState(item: VendorInvoiceItem): 'posted' | 'exception' | 'ready' {
			if (item.status === 'posted') { return 'posted'; }
			if (item.status === 'failed' || item.dry === 'fail') { return 'exception'; }
			return (this.vinvReady(item) && !item.unmatched.length) ? 'ready' : 'exception';
		}

		/** Plain-language reasons a row needs attention — shown as chips so the fix is obvious. */
		vinvIssues(item: VendorInvoiceItem): string[] {
			const out: string[] = [];
			if (item.loadingVendor) { out.push('Loading…'); return out; }
			if (item.duplicate) { out.push('Already entered'); }
			if (this.vinvInBatchDup(item)) { out.push('Double scan'); }
			if (!item.vendor) { out.push('Match the vendor'); }
			if (!(item.fields.invoice_number || '').trim()) { out.push('Add invoice #'); }
			if (item.vendor && !item.grns.length) { out.push('Add a GRN line'); }
			if (item.unmatched.length) { out.push(item.unmatched.length + ' line(s) unmatched'); }
			if (item.vendor && item.grns.length && !this.vinvReconciles(item)) { out.push('Totals differ'); }
			if (item.status === 'failed') { out.push('Post failed'); }
			if (item.dry === 'fail') { out.push('Dry run would fail'); }
			if (item.grns.some(g => this.vinvGrnZeroRecd(g))) { out.push('GRN shows 0 received → posts to qty variance'); }
			if (item.grns.some(g => this.vinvGrnUnderInvoiced(g))) { out.push('Billing fewer units than received — confirm partial'); }
			return out;
		}

		/** A GRN line with nothing recorded as received: the std cost has no receipt accrual to
		 *  clear, so it posts to the qty-variance account instead of GRN clearing. Not a blocker —
		 *  it's a legit special/drop-ship path — but the operator should see it before posting. */
		vinvGrnZeroRecd(g: any): boolean {
			return (Number(g?.qtyrecd) || 0) <= 0;
		}

		/** Units still open on this GRN (received but not yet invoiced) when it was matched. */
		vinvGrnOpenQty(g: any): number {
			if (g?.qtyOpen != null) { return Number(g.qtyOpen) || 0; }
			return Math.max(0, (Number(g?.qtyrecd) || 0) - (Number(g?.quantityinv) || 0));
		}

		/** Total qty billed against one GRN across EVERY invoice in this batch. The vendor often splits
		 *  one receipt across two invoices (2 on one, 4 on another) that together fill it, so the
		 *  under-billed check has to look batch-wide, not just at this one line. */
		vinvGrnBatchBilled(grnno: any): number {
			const key = String(grnno);
			let sum = 0;
			for (const q of this.vinvQueue) {
				for (const g of (q.grns || [])) {
					if (String(g.grnno) === key) { sum += Number(g.toinvoice) || 0; }
				}
			}
			return Math.round(sum * 1000) / 1000;
		}

		/** How many invoices in this batch bill this GRN (>1 → it's split across invoices). */
		vinvGrnSharedCount(grnno: any): number {
			const key = String(grnno);
			let n = 0;
			for (const q of this.vinvQueue) {
				if ((q.grns || []).some((g: any) => String(g.grnno) === key)) { n++; }
			}
			return n;
		}

		/** This GRN is split across more than one invoice in the batch (show the "split" marker). */
		vinvGrnSplit(g: any): boolean {
			return !!g?.grnno && this.vinvGrnSharedCount(g.grnno) > 1;
		}

		/** Split GRN whose pieces across the batch fully fill the open qty — informational, not a problem. */
		vinvGrnSplitFilled(g: any): boolean {
			return this.vinvGrnSplit(g) && this.vinvGrnBatchBilled(g.grnno) >= this.vinvGrnOpenQty(g) - 0.0001;
		}

		/** Billing FEWER units than the GRN shows received-but-uninvoiced. The dollar total can still
		 *  reconcile (a mis-read "6 @ $74" → "4 @ $111" multiplies to the same line total), so the only
		 *  signal is the quantity gap. Legit for genuine partial shipments — hence a warning, not a block —
		 *  but it leaves units stranded in GRN suspense, so the operator must confirm it's intentional.
		 *  NOT a warning when the rest of the GRN is billed on OTHER invoices in this batch (a split fill). */
		vinvGrnUnderInvoiced(g: any): boolean {
			const open = this.vinvGrnOpenQty(g);
			const toinv = Number(g?.toinvoice) || 0;
			if (!((Number(g?.qtyrecd) || 0) > 0 && toinv > 0 && toinv < open)) { return false; }
			return this.vinvGrnBatchBilled(g.grnno) < open - 0.0001;
		}

		/** Body auto-opens only for exceptions; ready/posted rows stay collapsed unless clicked. */
		vinvShowBody(item: VendorInvoiceItem): boolean {
			// Tri-state: once the operator clicks, their choice wins; until then exceptions auto-open.
			if (item.expanded != null) { return item.expanded; }
			return this.vinvState(item) === 'exception';
		}

		get vinvExceptionRows(): VendorInvoiceItem[] { return this.vinvQueue.filter(q => this.vinvState(q) === 'exception'); }
		get vinvReadyRows(): VendorInvoiceItem[] { return this.vinvQueue.filter(q => this.vinvState(q) === 'ready'); }
		/** Anything we can (re)post right now: reconciles + matched, not already posted or mid-post.
		 *  Includes a previously-failed row so a fixed cause can be retried from the bulk button. */
		get vinvPostableRows(): VendorInvoiceItem[] {
			return this.vinvQueue.filter(q => this.vinvReady(q) && q.status !== 'posted' && q.status !== 'posting');
		}
		get vinvPostedRows(): VendorInvoiceItem[] { return this.vinvQueue.filter(q => this.vinvState(q) === 'posted'); }
		/** The active working list: every row EXCEPT the ones already posted — a posted invoice is done,
		 *  so it drops out of the cards (its record still lives in the batch journal and the Posted count).
		 *  Stable order (by id) so a row never flops around as the operator edits it; 'posting' rows stay
		 *  visible so the operator sees the post in flight. Pair with the trackBy so DOM nodes are reused. */
		get vinvSortedRows(): VendorInvoiceItem[] { return this.vinvQueue.filter(q => q.status !== 'posted'); }
		vinvTrackById(_i: number, item: VendorInvoiceItem): number { return item.id; }

		/** Cycle through the rows that need attention: expand the next one and scroll it into view. */
		private vinvIssueCursor = -1;
		jumpToNextIssue(): void {
			const ex = this.vinvExceptionRows;
			if (!ex.length) { return; }
			this.vinvIssueCursor = (this.vinvIssueCursor + 1) % ex.length;
			const item = ex[this.vinvIssueCursor];
			item.expanded = true;
			setTimeout(() => {
				const el = document.getElementById('vinv-card-' + item.id);
				if (el && el.scrollIntoView) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
			}, 60);
		}

		vinvReviewReason(item: VendorInvoiceItem): string {
			if (item.status === 'posted') { return 'Posted'; }
			if (item.status === 'failed') { return 'Failed'; }
			if (item.duplicate) { return 'Already entered' + (item.dupTransno ? ' #' + item.dupTransno : ''); }
			if (this.vinvInBatchDup(item)) { return 'Double scan'; }
			if (!item.vendor) { return 'Match vendor'; }
			if (!(item.fields.invoice_number || '').trim()) { return 'Missing invoice #'; }
			if (!item.grns.length) { return 'Add GRN lines'; }
			if (!this.vinvReconciles(item)) { return 'Totals differ'; }
			if (item.dry === 'pass') { return 'Preview OK'; }
			return 'Ready';
		}

	/** Cross-reference (supplier, invoice #) against posted supptrans — the same dup guard the
	 *  traditional entry screen uses — so an already-entered invoice is flagged and can't re-post. */
	private checkDuplicate(item: VendorInvoiceItem): void {
		// A posted row is the source of its own supptrans record — never re-flag it as a duplicate
		// of itself (otherwise a batch post lights up every earlier row as "already entered").
		if (item.status === 'posted') { item.duplicate = false; item.dupPosted = undefined; item.dupTransno = undefined; return; }
		const sid = item.vendor_match?.supplierid;
		const ref = (item.fields.invoice_number || '').trim();
		if (!sid || !ref) { item.duplicate = false; item.dupPosted = undefined; item.dupTransno = undefined; return; }
		this.vendorService.checkInvoiceReference(sid, ref).subscribe((r: any) => {
			if (item.status === 'posted') {
				item.duplicate = false;
				item.dupPosted = undefined;
				item.dupTransno = undefined;
				return;
			}
			const dup = !!(r && r.used);
			const posted = dup ? (Number(r.posted_total) || 0) : undefined;
			const transno = dup ? (r.transno || '') : undefined;
			if (dup !== item.duplicate || posted !== item.dupPosted || transno !== item.dupTransno) {
				item.duplicate = dup;
				item.dupPosted = posted;
				item.dupTransno = transno;
				this.persist();
			}
		});
	}

	/** Operator edited the invoice # — persist and re-run the duplicate check. */
	onInvoiceNoEdited(item: VendorInvoiceItem): void {
		this.persist();
		this.checkDuplicate(item);
	}

	// ── Batch journal (reconcile the screen against the physical stack) ──
	get vinvInvoiceTotal(): number {
		return this.vinvQueue.reduce((s, q) => s + (Number(q.fields.total_amount) || 0), 0);
	}
	get vinvEnteredTotal(): number {
		return this.vinvQueue.reduce((s, q) => s + this.vinvEnteredDisplay(q), 0);
	}
	get vinvPostedCount(): number { return this.vinvQueue.filter(q => q.status === 'posted').length; }
	get vinvDupCount(): number { return this.vinvQueue.filter(q => q.status !== 'posted' && q.duplicate).length; }
	get vinvMismatchCount(): number { return this.vinvQueue.filter(q => !this.vinvReconciles(q)).length; }

	/** True when ANOTHER row in this batch is the SAME invoice — same vendor, invoice #, PO set AND
	 *  lines (a genuine double scan, e.g. the same invoice dropped twice as separate files). Same
	 *  invoice # with different lines is NOT a dup — continuation pages already merge on intake. */
	vinvInBatchDup(item: VendorInvoiceItem): boolean {
		const sid = item.vendor_match?.supplierid || '';
		const ref = (item.fields.invoice_number || '').trim().toUpperCase();
		if (!ref) { return false; }
		return this.vinvQueue.some(q => q !== item
			&& (q.vendor_match?.supplierid || '') === sid
			&& (q.fields.invoice_number || '').trim().toUpperCase() === ref
			&& this.sameInvoiceContent(q.fields, item.fields));
	}
	get vinvBatchDupCount(): number { return this.vinvQueue.filter(q => this.vinvInBatchDup(q)).length; }
	/** Whole-batch reconcile: does the sum of what will post equal the sum of stated invoice totals? */
	vinvReconcileBatch(): boolean {
		return Math.abs(this.vinvInvoiceTotal - this.vinvEnteredTotal) <= 0.01;
	}
	get vinvReadyCount(): number { return this.vinvPostableRows.length; }

	/** The server message worth showing the operator: post failure, post success ref, or dry-run failure. */
	vinvDetail(item: VendorInvoiceItem): string {
		if (item.status === 'failed') { return item.message || 'Post failed'; }
		if (item.status === 'posted') { return item.message || ''; }
		if (item.dry === 'fail') { return item.dryMessage || 'Dry run would fail'; }
		return '';
	}

	private vinvMoney(n: number): string {
		return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
	}

	private vinvExportStamp(): string {
		const d = new Date();
		const p = (n: number) => String(n).padStart(2, '0');
		return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
	}

	/** One row per queued invoice — mirrors the on-screen batch journal columns for export. */
	private vinvJournalRows(): Array<{ idx: number; invoice: string; vendor: string; source: string; invoiceAmt: number; enteredAmt: number; status: string; detail: string }> {
		return this.vinvQueue.map((q, i) => ({
			idx: i + 1,
			invoice: q.fields.invoice_number || '',
			vendor: q.vendor?.suppname || q.vendor_match?.suppname || '',
			source: (q.fileName || '') + (q.pages?.length ? ' p' + q.pages.join(', ') : (q.page ? ' p' + q.page : '')),
			invoiceAmt: Number(q.fields.total_amount) || 0,
			enteredAmt: this.vinvEnteredDisplay(q),
			status: this.vinvReviewReason(q),
			detail: this.vinvDetail(q),
		}));
	}

	exportVinvJournalXlsx(): void {
		const rows = this.vinvJournalRows();
		if (!rows.length) {
			this.globalSearchService.showNotification('No invoices in the batch to export', 'info', 'bottom', 'left');
			return;
		}
		const aoa: any[][] = [['#', 'Invoice', 'Vendor', 'Source', 'Invoice $', 'Entered $', 'Status', 'Detail']];
		rows.forEach(r => aoa.push([r.idx, r.invoice, r.vendor, r.source, r.invoiceAmt, r.enteredAmt, r.status, r.detail]));
		aoa.push([]);
		aoa.push(['', '', '', 'Grand total', this.vinvInvoiceTotal, this.vinvEnteredTotal, '', '']);
		const ws = XLSX.utils.aoa_to_sheet(aoa);
		ws['!cols'] = [{ wch: 4 }, { wch: 18 }, { wch: 28 }, { wch: 26 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 40 }];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Batch journal');
		XLSX.writeFile(wb, 'vendor-invoice-batch-journal-' + this.vinvExportStamp() + '.xlsx');
	}

	exportVinvJournalPdf(): void {
		const rows = this.vinvJournalRows();
		if (!rows.length) {
			this.globalSearchService.showNotification('No invoices in the batch to export', 'info', 'bottom', 'left');
			return;
		}
		const doc = new jsPDF({ orientation: 'landscape' });
		doc.setFontSize(14);
		doc.text('Vendor Invoice Batch Journal', 14, 16);
		doc.setFontSize(9);
		doc.text(new Date().toLocaleString(), 14, 22);
		autoTable(doc, {
			startY: 26,
			head: [['#', 'Invoice', 'Vendor', 'Source', 'Invoice $', 'Entered $', 'Status', 'Detail']],
			body: rows.map(r => [r.idx, r.invoice, r.vendor, r.source, this.vinvMoney(r.invoiceAmt), this.vinvMoney(r.enteredAmt), r.status, r.detail]),
			foot: [['', '', '', 'Grand total', this.vinvMoney(this.vinvInvoiceTotal), this.vinvMoney(this.vinvEnteredTotal), '', '']],
			styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
			headStyles: { fillColor: [33, 33, 33] },
			footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
			columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' } },
		});
		doc.save('vendor-invoice-batch-journal-' + this.vinvExportStamp() + '.pdf');
	}

	/** A POST needs the row fully ready; a PREVIEW (dry run) only needs a vendor + GRN lines so the
	 *  operator can see the GL impact while the row is still being reconciled. */
	private buildVendorPayload(item: VendorInvoiceItem, forPreview = false): any | null {
		const enough = forPreview ? (!!item.vendor && item.grns.length > 0) : this.vinvReady(item);
		if (!enough) { return null; }
		return this.documentIntakeService.buildVendorInvoicePayload(
			item.vendor, item.fields, item.grns, this.vinvEffectiveDiscount(item), this.isTires(), this.currentUser,
		);
	}

	/** Per-row dry run: validate + preview the GL posting via glpreview (writes nothing, no sequence burnt). */
	vinvDryRun(item: VendorInvoiceItem): Promise<void> { return this.runVendor(item, true); }
	vinvPost(item: VendorInvoiceItem): Promise<void> { return this.runVendor(item, false); }

	private async runVendor(item: VendorInvoiceItem, dryRun: boolean): Promise<void> {
		if (item.status === 'posting') { return; }
		const payload = this.buildVendorPayload(item, dryRun);
		if (!payload) {
			this.globalSearchService.showNotification(
				dryRun ? 'Match a vendor and add at least one GRN line to preview' : 'Match a vendor, GRN line and reference first',
				'warning', 'bottom', 'left');
			return;
		}
		if (!dryRun) { item.status = 'posting'; item.message = ''; }
		try {
			if (dryRun) {
				const res: any = await this.vendorService.previewInvoiceGl(payload).toPromise();
				if (res?.success) {
					item.dry = 'pass'; item.dryMessage = 'Preview passed'; item.glPreview = res.lines || []; item.expanded = true;
				} else {
					item.dry = 'fail'; item.dryMessage = res?.message || 'Preview failed'; item.glPreview = [];
				}
			} else {
				const res: any = await this.vendorService.createInvoice(payload).toPromise();
				if (res?.success) {
					item.status = 'posted'; item.message = '#' + (res.transno || res.id || ''); item.dry = undefined;
					item.duplicate = false; item.dupPosted = undefined; item.dupTransno = undefined;
					// A just-posted invoice makes any UNPOSTED twin in the batch a real duplicate now.
					// Skip already-posted rows — they'd otherwise re-match their own supptrans record.
					this.vinvQueue.forEach(q => { if (q !== item && q.status !== 'posted') { this.checkDuplicate(q); } });
				} else {
					item.status = 'failed'; item.message = res?.message || 'Post failed';
				}
			}
		} catch {
			if (dryRun) { item.dry = 'fail'; item.dryMessage = 'Server error'; }
			else { item.status = 'failed'; item.message = 'Server error'; }
		}
		this.persist();
	}

	vinvDryRunAll(): Promise<void> { return this.runVendorReady(true); }
	vinvPostAllReady(): Promise<void> { return this.runVendorReady(false); }

	private async runVendorReady(dryRun: boolean): Promise<void> {
		if (this.vinvPosting) { return; }
		const targets = this.vinvPostableRows;
		if (!targets.length) {
			this.globalSearchService.showNotification('No ready invoices to run', 'info', 'bottom', 'left');
			return;
		}
		this.vinvPosting = true;
		this.vinvRunMode = dryRun ? 'dry' : 'live';
		this.vinvPosted = 0;
		this.vinvFailed = 0;
		this.vinvTotal = targets.length;
		for (const item of targets) {
			await this.runVendor(item, dryRun);
			const ok = dryRun ? item.dry === 'pass' : item.status === 'posted';
			if (ok) { this.vinvPosted++; } else { this.vinvFailed++; }
		}
		this.vinvPosting = false;
		const verb = dryRun ? 'Preview' : 'Post';
		this.globalSearchService.showNotification(
			`${verb}: ${this.vinvPosted} ${dryRun ? 'previewed' : 'posted'}, ${this.vinvFailed} ${dryRun ? 'need review' : 'failed'}`,
			this.vinvFailed ? 'warning' : 'success', 'bottom', 'left'
		);
	}

	// ── Manual GRN add (when the auto-match misses, pick from the vendor's open GRN pool) ──
	vinvPoolFilter: { [itemId: number]: string } = {};

	/** Open GRN count loaded for this row's vendor (shown in the raw/debug strip). */
	vinvPoolSize(item: VendorInvoiceItem): number {
		const sid = item.vendor_match?.supplierid;
		return sid ? (this.grnPoolCache[sid] || []).length : 0;
	}

	/** The vendor's open GRN lines not already on this invoice, filtered by the row's search box. */
	vinvFilteredPool(item: VendorInvoiceItem): any[] {
		const sid = item.vendor_match?.supplierid;
		const pool = sid ? (this.grnPoolCache[sid] || []) : [];
		const used = new Set(item.grns.map(g => String(g.grnno)));
		const raw = (this.vinvPoolFilter[item.id] || '').trim();
		let rows = pool.filter(g => !used.has(String(g.grnno)));
		if (raw) {
			const q = raw.toLowerCase();
			const qNorm = this.documentIntakeService.normCode(raw);   // alnum only, for part/reference #s
			rows = rows.filter(g => {
				// Search across item code, description, GRN#, PO#, SO#/job ref, ship ref, stock id and reference.
				const hay = `${g.itemcode || ''} ${g.itemdescription || ''} ${g.grnno || ''} ${g.orderno || ''} ${g.shiptref || ''} ${g.jobref || ''} ${g.stockid || ''} ${g.reference || ''}`;
				if (hay.toLowerCase().indexOf(q) >= 0) { return true; }
				// Punctuation-insensitive part-number match: "FAL-28953727" finds "FAL28953727" / "28953727".
				return !!qNorm && this.documentIntakeService.normCode(hay).indexOf(qNorm) >= 0;
			});
		}
		return rows.slice(0, 25);
	}

	/** Add a pooled GRN line to this invoice, pulling the invoice's qty/price for its matching item. */
	addGrnToItem(item: VendorInvoiceItem, g: any): void {
		if (item.grns.some(x => String(x.grnno) === String(g.grnno))) { return; }
		const gkeys = this.documentIntakeService.codeKeys(g.itemcode);
		const line = (item.fields.lines || []).find(l => {
			const lk = [...this.documentIntakeService.codeKeys(l.itemcode), ...this.documentIntakeService.codeKeys(l.description)];
			return lk.some(k => gkeys.indexOf(k) >= 0);
		});
		item.grns = [...item.grns, this.documentIntakeService.grnRowForInvoice(g, line?.qty, line?.unit_price)];
		item.dry = undefined; item.dryMessage = '';
		this.persist();
	}

	/**
	 * No-PO intake for an invoice that arrived without a purchase order: open the same modal the
	 * traditional entry screen uses to turn this vendor's no-PO receipts (stock movements) into a
	 * PO + GRN on the fly. On success, re-warm the vendor's GRN pool and re-run the auto-match so the
	 * freshly created GRN lines drop onto this invoice — the operator reviews and posts as usual.
	 */
	vinvOpenNoPo(item: VendorInvoiceItem): void {
		const supplierid = item.vendor_match?.supplierid;
		if (!supplierid) {
			this.globalSearchService.showNotification('Match the vendor first, then add no-PO items', 'warning', 'bottom', 'left');
			return;
		}
		const ref = this.dialog.open(NoPoItemsModalComponent, {
			width: '90%',
			maxWidth: '1400px',
			data: { supplierid },
		});
		// GRN numbers the no-PO action just created (filled from the modal result), plus the pool as it
		// stood before, so we can append ONLY the new lines and never disturb what's already on the invoice.
		const createdGrnNos = new Set<string>();
		const priorGrnNos = new Set<string>((this.grnPoolCache[supplierid] || []).map((g: any) => String(g.grnno)));
		ref.afterClosed().subscribe((result: any) => {
			if (!result || !result.success) { return; }
			(result.createdGRNs || []).forEach((x: any) => {
				const no = String(x?.grnno ?? x?.grnNo ?? x?.grn ?? x ?? '');
				if (no !== '') { createdGrnNos.add(no); }
			});
			// New PO/GRN lines now exist for this vendor — reload the pool, then MERGE: keep every line
			// already on the invoice (PO-backed or hand-added, with the operator's qty/price edits) and
			// APPEND only the GRN lines this no-PO action just created. A mixed invoice can then carry
			// both PO-backed and no-PO lines together. (Old behavior replaced item.grns wholesale, which
			// wiped any GRN you'd added before opening this modal.)
			this.purchasingService.getGrnDetails(supplierid).subscribe(
				(r: any) => {
					const pool = Array.isArray(r) ? r : [];
					this.grnPoolCache[supplierid] = pool;
					const have = new Set(item.grns.map((g: any) => String(g.grnno)));
					const created = pool.filter((g: any) => {
						const no = String(g.grnno);
						if (have.has(no)) { return false; }                       // already on the invoice
						return createdGrnNos.size ? createdGrnNos.has(no) : !priorGrnNos.has(no);  // new since this action
					});
					created.forEach((g: any) => this.addGrnToItem(item, g));       // reuses the dedup + qty/price mapping
					// Refresh the advisory matched/unmatched display from the full pool WITHOUT dropping lines.
					const m = this.documentIntakeService.matchInvoiceToGrns(item.fields, pool);
					item.matchedCodes = m.matchedCodes;
					item.unmatched = m.unmatched;
					item.dry = undefined; item.dryMessage = '';
					this.checkDuplicate(item);
					this.persist();
					this.globalSearchService.showNotification(
						`PO / GRN created — ${created.length} no-PO line(s) added, existing lines kept`, 'success', 'bottom', 'left');
				},
				() => this.globalSearchService.showNotification('Created, but could not reload GRNs — reopen the invoice', 'warning', 'bottom', 'left')
			);
		});
	}

	/** Raw AI extraction for this row, pretty-printed for the operator to inspect / debug.
	 *  Prefers the unprocessed model row the server passed through; falls back to the parsed fields. */
	vinvRaw(item: VendorInvoiceItem): string {
		try { return JSON.stringify(item.fields?.raw ?? item.fields, null, 2); } catch { return ''; }
	}

	vinvRemove(item: VendorInvoiceItem): void {
		this.vinvQueue = this.vinvQueue.filter(q => q.id !== item.id);
		this.persist();
	}
	vinvClearQueue(): void {
		this.vinvQueue = this.vinvQueue.filter(q => q.status === 'posting');
		this.persist();
	}

	// Manual vendor pick for an unmatched invoice row (reuses the file-vendor search plumbing).
	vinvVendorResults: any[] = [];
	private vinvVendorSub: any;
	searchRowVendor(term: string): void {
		const q = (term || '').trim();
		if (this.vinvVendorSub) { this.vinvVendorSub.unsubscribe?.(); }
		if (q.length < 2) { this.vinvVendorResults = []; return; }
		this.vinvVendorSub = this.vendorService.getVendorSearch(q).subscribe((r: any) => {
			let rows: any[] = [];
			if (Array.isArray(r)) { rows = r; }
			else if (Array.isArray(r?.rows)) { rows = r.rows; }
			else if (Array.isArray(r?.vendors)) { rows = r.vendors; }
			else if (Array.isArray(r?.suppliers)) { rows = r.suppliers; }
			this.vinvVendorResults = rows.slice(0, 25);
		});
	}
}
